/**
 * Das Messkonzept einer Anlage — der Bereich aus
 * `ui_kits/dashboard/MesskonzeptScreen.jsx` (HM-012, überarbeitet mit HM-114).
 *
 * **Gesteckt wird in Steckplätze, nicht in Eingänge.** Der Nutzer wählt eine
 * Vorlage; die bringt ihre Steckplätze mit („Netzzähler", „Erzeugungszähler"),
 * und in die Steckplätze kommen Zähler. Ein Zweirichtungszähler füllt so mit
 * einem Handgriff zwei Eingänge der Rechnung.
 *
 * **Zwei Karten, wie der Entwurf sie anlegt.** Die erste trägt Kopf, Skizze und
 * die Auswahl, die aus einem Klick ins Bild folgt — beides auf zwei Karten
 * hieße, dass sich etwas außerhalb des Blickfelds öffnet —, darunter
 * eingeklappt die Verteilregel, der Rechenweg (die erste Formel steht in der
 * Zeile) und der Verlauf der Fassungen. Die zweite ist die Liste der
 * Steckplätze: offene zuerst, fertige eingeklappt.
 *
 * **Ein belegter Steckplatz öffnet erst die Ansicht, nicht die Wahl.** Ein
 * Wechsel ändert die Rechnung ab einem Zeitpunkt und darf nicht aus einem
 * Fehlklick entstehen; wer einen Zähler ersetzt oder eine Zuordnung aufhebt,
 * bestätigt das ausdrücklich.
 *
 * **Übernommen ist der Aufbau, nicht jede Zusage** — dieselbe Regel wie bei
 * jeder Übernahme aus dem Kit. Vier Dinge fehlen mit Absicht:
 *
 * Die **Spartenreiter** Wärme und Wasser: Es gibt für beide weder Zähler noch
 * Vorlagen; ein Reiter, hinter dem nichts kommt, ist die Zusage, gegen die jede
 * Übernahme geprüft wird. Der Kopf sagt „Messkonzept Strom".
 *
 * **Übernommen ist dagegen der Kasten „Verbraucher" unter „Verbrauch im Haus"**:
 * Die Verbraucher einer Hausvorlage (HM-110) — eine Wallbox, eine Wärmepumpe
 * mit eigenem Zähler — sind genau diese Aufschlüsselung. Sie stehen dort in
 * der Skizze und in einer eigenen Zeile der Liste und werden dort hinterlegt;
 * im Mieterstrom bleiben sie Steckplätze, weil sie dort Summanden sind.
 *
 * **Vorgemerkte Änderungen** mit einem Zeitpunkt in der Zukunft: Der Server
 * nimmt `validFrom` nur bis jetzt an. Ein Hinweis „geplant ab" wäre eine
 * Änderung, die niemand ausführt.
 *
 * Das **Änderungsprotokoll** mit Sätzen je Handgriff: Der Server führt keines,
 * sondern Fassungen. Der Verlauf liest den Satz aus dem Unterschied zweier
 * Fassungen ab (`lib/concept-history.ts`), statt ihn zu erfinden.
 *
 * **Mehrfachbelegung** gibt es seit HM-080 als Steckplatz-Familien: je
 * Wohneinheit, je weiterem Verbraucher und je Erzeuger ein Steckplatz. Die
 * Steckplätze einer Fassung holt die Seite deshalb vom Server
 * (`GET /concepts/{id}/template`), expandiert; die Skizze fasst sie ab vier
 * Abzweigen zu einem Kasten zusammen, der ihre Aufzählung öffnet.
 *
 * **Jede Änderung ist eine neue Fassung**, und der Nutzer sagt, ab wann sie
 * gilt: ab jetzt (ein Umbau), seit Beginn (eine Korrektur) oder seit einem
 * Zeitpunkt. Der Unterschied entscheidet, ob die Vergangenheit neu gerechnet
 * wird, und er lässt sich aus der Änderung nicht ablesen.
 */
import { useId, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Select,
  icons,
} from "@hausmeter/ui";
import { t } from "@hausmeter/i18n";
import { formatInstant } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { SectionHeading } from "../components/section.js";
import {
  ConceptSketch,
  HOUSE_KEY,
  addKey,
  behindKey,
  type SketchPick,
  assetsKey,
} from "../components/concept-sketch.js";
import {
  assetKindsOf,
  conceptStructure,
  type ConceptRole,
  isAssetSlot,
  isGenerationSlot,
} from "../lib/concept-roles.js";
import { navigate } from "../lib/router.js";
import { consumersBehindHouse } from "../lib/concept-sketch-rest.js";
import { Disclosure } from "../components/disclosure.js";
import { HintMark } from "../components/hint-mark.js";
import {
  conceptHistory,
  conceptSince,
  firstConceptStart,
  latestEffectiveChange,
  type ConceptChange,
} from "../lib/concept-history.js";
import {
  assignmentOf,
  assignmentWith as assignmentWithFor,
  droppedOccupiedSlots,
  meterHasRole as hasRole,
  meteringPointOfSlot,
  resolveChosenMeter,
  roleForInput,
  type Assignment,
  type Role,
  intakeChange,
  intakeChangeTexts,
  isIntakeInput,
} from "../lib/concept-editor.js";
import { saveWithUnitMoves, stuckMovesMessage, type UnitMove } from "../lib/unit-moves.js";
import { instantOfSiteLocalInput, siteLocalInputValue } from "../lib/site-time.js";
import { formatTimeAgo } from "../lib/format.js";
import { useSiteAccess } from "../lib/site-access.js";
import { meterKindLabel, registerRoleLabel, meterNumberText } from "../lib/meter-labels.js";
import { formatValue } from "@hausmeter/i18n";
import {
  useConceptTemplate,
  useConceptTemplates,
  useMeterSuggestions,
  useMeteringPoints,
  useMeters,
  useSiteAreas,
  useSiteConcepts,
  useSiteLive,
  useGenerationAssets,
} from "../lib/queries.js";
import {
  GenerationAssetDialog,
  kindLabel,
  orientationText,
  ratingText,
} from "./generation-assets.js";
import {
  distributionDescription,
  distributionLabel,
  type PvDistribution,
} from "../lib/distribution.js";

type Concept = ApiComponents["schemas"]["MeasurementConcept"];
type Template = ApiComponents["schemas"]["ConceptTemplate"];
type Slot = ApiComponents["schemas"]["ConceptTemplateSlot"];
type Meter = ApiComponents["schemas"]["Meter"];
type MeterSuggestion = ApiComponents["schemas"]["MeterSuggestion"];
type LiveValue = ApiComponents["schemas"]["LiveValue"];
type Layout = ApiComponents["schemas"]["ConceptLayout"];
type LayoutConsumer = ApiComponents["schemas"]["ConceptLayoutConsumer"];
type ConsumerKind = LayoutConsumer["kind"];
type ConsumerPosition = NonNullable<LayoutConsumer["position"]>;
type SiteArea = ApiComponents["schemas"]["SiteArea"];

const CONSUMER_KINDS: readonly ConsumerKind[] = ["common_area", "heat_pump", "wallbox", "other"];
const CONSUMER_POSITIONS: readonly ConsumerPosition[] = ["before_own_unit", "behind_own_unit"];

const MONO = { fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" } as const;
const CAP = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: ".07em",
  textTransform: "uppercase",
  color: "var(--text-faint)",
} as const;
const MUTED = { fontSize: 12.5, lineHeight: 1.55, color: "var(--text-muted)" } as const;

/** Die Eingänge eines Steckplatzes, an denen der Nutzer das Zählwerk wählen kann. */
function choosableInputs(template: Template, slot: Slot, meter: Meter | null) {
  return template.inputs.filter(
    (input) =>
      input.slot === slot.key &&
      input.roles.length > 1 &&
      (meter === null || input.roles.filter((role) => hasRole(meter, role)).length > 1),
  );
}

/**
 * Die Eingänge, die ein Zähler im Steckplatz bedienen muss: die Pflicht-Eingänge
 * — ein **optionaler** daneben (die Einspeisung bei „Nur Netzbezug", HM-171)
 * darf fehlen, ein Einrichtungszähler zählt sie nicht. Hat der Steckplatz nur
 * optionale Eingänge, muss er sie bedienen, sonst steckte man ihn für nichts.
 */
function neededInputs(template: Template, slot: Slot) {
  const ofSlot = template.inputs.filter((input) => input.slot === slot.key);
  return ofSlot.some((input) => input.required) ? ofSlot.filter((input) => input.required) : ofSlot;
}

/** Ob ein Zähler für jeden nötigen Eingang des Steckplatzes eines der zulässigen Zählwerke hat. */
function fitsSlot(meter: Meter, template: Template, slot: Slot): boolean {
  return neededInputs(template, slot).every((input) =>
    input.roles.some((role) => hasRole(meter, role)),
  );
}

/** Dieselbe Prüfung für einen Vorschlag: Führte der Zähler daraus, was der Steckplatz braucht? */
function suggestionFitsSlot(suggestion: MeterSuggestion, template: Template, slot: Slot): boolean {
  return neededInputs(template, slot).every((input) =>
    input.roles.some((role) => suggestion.registers.some((register) => register.role === role)),
  );
}

export function ConceptScreen({
  siteId,
  siteTimeZone,
}: {
  siteId: string | null;
  /** Die Zeitzone der Anlage — für „gültig seit", nie die des Browsers. */
  siteTimeZone: string;
}) {
  const templates = useConceptTemplates();
  const concepts = useSiteConcepts(siteId);
  /* Die Stände der Zählwerke — damit die Wahl „Bezug oder Einspeisung" an
     einem Zähler nicht geraten werden muss: Das Zählwerk, das steigt, ist das. */
  const live = useSiteLive(siteId);
  /* Auch abgelöste und archivierte Zähler: Ein Konzept kann auf eine Messstelle
     zeigen, an der gerade keiner hängt — der Steckplatz soll dann den letzten
     nennen und nicht „kein Zähler zugeordnet" behaupten. Zur Wahl stehen nur
     die, die gerade hängen. */
  const meters = useMeters(siteId, true);
  /* Für die Familien-Vorlagen: welche Wohneinheiten es heute gibt — damit die
     Seite sagen kann, welche noch nicht im Konzept stecken. */
  const areas = useSiteAreas(siteId);
  /**
   * Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10): Wer nur lesen
   * darf, sieht Bild, Liste und Rechenweg — und keinen Weg, etwas zu stecken.
   */
  const readOnly = !useSiteAccess().canWrite;

  const active = concepts.data?.concepts.find((concept) => concept.status === "active") ?? null;
  /**
   * **Die Vorlage der Fassung kommt vom Server, expandiert** (HM-080). Der
   * Katalog kennt bei einer Familien-Vorlage nur die Familie; die Steckplätze
   * dieser Fassung — je Wohneinheit einer — kennt nur der Server. Bei einer
   * statischen Vorlage ist es dieselbe Auskunft.
   */
  const expanded = useConceptTemplate(active?.templateKey === null ? null : (active?.id ?? null));
  const template = active?.templateKey === null || active === null ? null : (expanded.data ?? null);

  if (siteId === null) return null;
  if (
    templates.isPending ||
    concepts.isPending ||
    (active !== null && active.templateKey !== null && expanded.isPending)
  ) {
    return <p style={MUTED}>Wird geladen …</p>;
  }
  if (templates.isError || concepts.isError || expanded.isError) {
    return <Alert tone="danger">Das Messkonzept konnte nicht geladen werden.</Alert>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)", maxWidth: 1100 }}>
      {active !== null && active.templateKey === null ? (
        <FreeConcept
          siteId={siteId}
          concept={active}
          templates={templates.data.templates}
          firstStart={firstConceptStart(concepts.data.concepts) ?? active.validFrom}
          timeZone={siteTimeZone}
          readOnly={readOnly}
        />
      ) : active === null || template === null ? (
        <NoConcept siteId={siteId} templates={templates.data.templates} readOnly={readOnly} />
      ) : (
        <ConceptEditor
          siteId={siteId}
          concept={active}
          concepts={concepts.data.concepts}
          firstStart={firstConceptStart(concepts.data.concepts) ?? active.validFrom}
          template={template}
          templates={templates.data.templates}
          meters={(meters.data?.items ?? []).filter((meter) => meter.commodity === "electricity")}
          liveValues={live.data?.values ?? []}
          areas={areas.data?.items ?? []}
          timeZone={siteTimeZone}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * **Ein freies Messkonzept** — ein Graph ohne Vorlage (`templateKey: null`).
 * Diese Seite kann ihn nicht bearbeiten: Skizze und Steckplätze kommen aus
 * einer Vorlage, und ein freier Graph hat keine. „Noch kein Messkonzept" wäre
 * aber falsch — die Anlage rechnet, und wer hier eine Vorlage wählt, löst eine
 * laufende Rechnung ab. Deshalb dieselbe Frage wie bei jedem Konzeptwechsel:
 * ab wann, und mit Haken (HM-190).
 */
function FreeConcept({
  siteId,
  concept,
  templates,
  firstStart,
  timeZone,
  readOnly,
}: {
  siteId: string;
  concept: Concept;
  templates: readonly Template[];
  firstStart: string;
  timeZone: string;
  readOnly: boolean;
}) {
  return (
    <>
      <Card>
        <EmptyState title="Eigenes Messkonzept" icon={<icons.IconTemplate size={30} />} size="md">
          Diese Anlage rechnet nach einem eigenen Messkonzept „{concept.name}", das ohne Vorlage
          eingerichtet wurde. Es gilt seit{" "}
          {formatInstant(new Date(concept.validFrom), timeZone, "de-DE")} und lässt sich hier nicht
          bearbeiten.
          {readOnly
            ? ""
            : " Du kannst es durch eine Vorlage ersetzen — ab einem Zeitpunkt, den du wählst."}
        </EmptyState>
      </Card>
      {!readOnly && (
        <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
          <SectionHeading
            title="Durch eine Vorlage ersetzen"
            subtitle="Die Anlagenformen, die dein Konto führen darf. Die Rechnung steht bei jeder dabei."
          />
          <TemplatePicker
            siteId={siteId}
            templates={templates}
            current={null}
            replacesActive
            since={firstStart}
            latestChange={concept.validFrom}
            timeZone={timeZone}
          />
        </section>
      )}
    </>
  );
}

function NoConcept({
  siteId,
  templates,
  readOnly,
}: {
  siteId: string;
  templates: readonly Template[];
  readOnly: boolean;
}) {
  if (readOnly) {
    return (
      <Card>
        <EmptyState title="Noch kein Messkonzept" icon={<icons.IconTemplate size={30} />} size="md">
          Ein Messkonzept sagt, wie diese Anlage aus ihren Zählerständen rechnet. Für diese Anlage
          ist noch keines eingerichtet — das kann, wer sie bearbeiten darf.
        </EmptyState>
      </Card>
    );
  }
  return (
    <>
      <Card>
        <EmptyState title="Noch kein Messkonzept" icon={<icons.IconTemplate size={30} />} size="md">
          Ein Messkonzept sagt, wie diese Anlage aus ihren Zählerständen rechnet — was Verbrauch
          ist, was Erzeugung, was ins Netz ging. Ohne eines zeigt „Strom &amp; Erzeugung" keine
          Werte. Wähle die Vorlage, die zu deinem Haus passt; die Zähler steckst du danach.
        </EmptyState>
      </Card>
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
        <SectionHeading
          title="Vorlage wählen"
          subtitle="Die Anlagenformen, die dein Konto führen darf. Die Rechnung steht bei jeder dabei."
        />
        <TemplatePicker siteId={siteId} templates={templates} current={null} />
      </section>
    </>
  );
}

/**
 * Die Vorlagen zur Wahl. **Eine Wahl legt ein neues Konzept an** und löst das
 * bisherige ab — das ist fachlich richtig: Die Anlage rechnet ab dann anders.
 *
 * **Ein Wechsel fragt nach dem Zeitpunkt, bevor er greift** — dieselbe Frage wie
 * bei einem Zählerwechsel, denn es ist dieselbe Bewegung. Ohne bisheriges
 * Konzept gibt es nichts abzulösen; dort legt der Klick an.
 */
function TemplatePicker({
  siteId,
  templates,
  current,
  replacesActive = current !== null,
  since,
  latestChange,
  timeZone,
  onDone,
}: {
  siteId: string;
  templates: readonly Template[];
  current: Template["key"] | null;
  /**
   * Es gibt ein laufendes Konzept, auch wenn es keine Vorlage ist (ein freier
   * Graph): Dann legt der Klick nicht an, sondern fragt wie bei einem Wechsel.
   */
  replacesActive?: boolean;
  /** Beginn des Messkonzepts — nur beim Wechsel. */
  since?: string;
  latestChange?: string;
  timeZone?: string;
  onDone?: () => void;
}) {
  const client = useQueryClient();
  const [pending, setPending] = useState<Template["key"] | null>(null);
  const create = useMutation({
    mutationFn: async (input: { templateKey: Template["key"]; validFrom: string | null }) => {
      const chosen = templates.find((entry) => entry.key === input.templateKey);
      const { data, error, response } = await api.POST("/sites/{siteId}/concepts", {
        params: { path: { siteId } },
        /* Eine Familien-Vorlage bekommt ein leeres Layout: keine Verbraucher,
           ein Erzeuger; die Wohneinheiten nimmt der Server aus der Anlage. */
        body: {
          templateKey: input.templateKey,
          inputs: {},
          ...(input.validFrom === null ? {} : { validFrom: input.validFrom }),
          ...(chosen?.layout === null || chosen === undefined
            ? {}
            : {
                layout: {
                  consumers: [],
                  generatorCount: 1,
                  generationIntake: false,
                  distribution: "dynamic",
                },
              }),
        },
      });
      if (error || !data)
        throw new Error(apiErrorMessage(error) ?? `Die API antwortete mit ${response.status}.`);
      return data;
    },
    onSuccess: async () => {
      setPending(null);
      await client.invalidateQueries({ queryKey: ["concepts", siteId] });
      await client.invalidateQueries({ queryKey: ["site-energy", siteId] });
      onDone?.();
    },
  });
  const pendingTemplate = templates.find((entry) => entry.key === pending) ?? null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-2)" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
          gap: "var(--sp-2)",
        }}
      >
        {templates.map((entry) => {
          const isCurrent = entry.key === current;
          const selected = isCurrent || entry.key === pending;
          return (
            <button
              key={entry.key}
              type="button"
              disabled={create.isPending || isCurrent}
              onClick={() =>
                replacesActive
                  ? setPending(entry.key)
                  : create.mutate({ templateKey: entry.key, validFrom: null })
              }
              aria-pressed={selected}
              style={{
                textAlign: "left",
                display: "flex",
                flexDirection: "column",
                gap: 4,
                padding: "11px 13px",
                cursor: isCurrent ? "default" : "pointer",
                borderRadius: "var(--radius-md)",
                background: isCurrent ? "var(--surface-inset)" : "var(--surface-card)",
                border: `1px solid ${selected ? "var(--hm-green-500)" : "var(--border-subtle)"}`,
                fontFamily: "var(--font-body)",
                color: "inherit",
              }}
            >
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text-strong)" }}>
                {entry.label}
              </span>
              <span style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text-muted)" }}>
                {entry.description}
              </span>
              {isCurrent && (
                <span style={{ fontSize: 11.5, color: "var(--hm-green-700)", fontWeight: 600 }}>
                  gilt heute
                </span>
              )}
            </button>
          );
        })}
      </div>
      {pendingTemplate !== null && replacesActive && (
        <ValidFromChoice
          question="Ab wann gilt das neue Messkonzept?"
          note={`Ab diesem Zeitpunkt wird nach „${pendingTemplate.label}" gerechnet. Zeiträume davor bleiben nach dem bisherigen Konzept gerechnet.`}
          acknowledge="Mir ist bewusst, dass das neue Konzept ohne zugeordnete Zähler beginnt und erst rechnet, wenn seine Pflichtsteckplätze besetzt sind."
          submitLabel="Konzeptwechsel übernehmen"
          conceptStart={since ?? new Date().toISOString()}
          latestChange={latestChange}
          timeZone={timeZone ?? "UTC"}
          busy={create.isPending}
          onCommit={(validFrom) => create.mutate({ templateKey: pendingTemplate.key, validFrom })}
          onCancel={() => setPending(null)}
        />
      )}
      {create.isError && <Alert tone="danger">{create.error.message}</Alert>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/** Welcher Steckplatz gerade zur Wahl steht, was schon gewählt ist — und welches Zählwerk je Eingang. */
interface Choice {
  readonly slot: string;
  readonly meterId: string | null;
  readonly roles: Readonly<Record<string, Role>>;
}

/** Wo angeklickt wurde — der Bereich dazu erscheint dort und nicht außerhalb des Blickfelds. */
type PanelFrom = "schema" | "list";
interface GroupRef {
  readonly key: string;
  readonly label: string;
  readonly slots: readonly string[];
  /**
   * `group`: die Steckplätze einer Rolle. `house` und `behind`: die Verbraucher
   * hinter „Verbrauch im Haus" bzw. hinter dem Zähler einer Wohneinheit — dort
   * lassen sie sich auch hinterlegen.
   */
  readonly kind: "group" | "house" | "behind";
  /** Bei `behind`: die Wohneinheit, der ein neuer Verbraucher gehört. */
  readonly siteAreaId: string | null;
}
/**
 * Was unter der Skizze oder unter einer Zeile offen ist.
 *
 * **Ein belegter Steckplatz führt nicht in die Zählerwahl**, sondern zeigt, was
 * steckt (`info`). Ein Wechsel ändert die Rechnung ab einem Zeitpunkt und darf
 * nicht aus einem Fehlklick ins Bild entstehen; er ist ein eigener, benannter
 * Schritt dahinter. Eine Rolle mit mehreren Steckplätzen öffnet deren
 * Aufzählung (`group`), das Plus eines Strangs das Hinzufügen (`add`).
 */
type Panel =
  | ({ readonly from: PanelFrom; readonly mode: "group" } & GroupRef)
  | { readonly from: PanelFrom; readonly mode: "add"; readonly key: string; readonly role: string }
  /**
   * Die **Erzeugungsanlagen** hinter dem Zähler eines Erzeugungs-Steckplatzes
   * (HM-124): PV-Anlagen und Speicher, die er misst. Wie die Verbraucher eine
   * Aufschlüsselung, die die Bilanz nicht verändert — und deshalb wird sie
   * dort hinterlegt, wo sie im Bild steht.
   */
  | { readonly from: PanelFrom; readonly mode: "assets"; readonly slot: string }
  | {
      readonly from: PanelFrom;
      /**
       * `intake`: ob der Erzeugungszähler auch die **Aufnahme** zählt (#40) — was in die
       * Anlagen hinter ihm hineingeht, etwa ein Speicher, der lädt. Eine Änderung des Layouts
       * samt Zuordnung, gültig ab einem Zeitpunkt.
       */
      readonly mode: "info" | "choose" | "unassign";
      readonly slot: string;
      /** Gewählt über eine Gruppe der Skizze — dorthin führt „Zurück". */
      readonly group: GroupRef | null;
    };

const DEFAULT_LAYOUT: Layout = {
  consumers: [],
  generatorCount: 1,
  generationIntake: false,
  distribution: "dynamic",
};

function ConceptEditor({
  siteId,
  concept,
  concepts,
  firstStart,
  template,
  templates,
  meters,
  liveValues,
  areas,
  timeZone,
  readOnly,
}: {
  siteId: string;
  concept: Concept;
  /** Alle Fassungen — für „gültig ab" und den Verlauf. */
  concepts: readonly Concept[];
  /**
   * Beginn der ältesten Fassung über alle Vorlagen — nur für den
   * **Konzeptwechsel** „seit Beginn". Jede Änderung innerhalb des Konzepts
   * nimmt `conceptSince` (HM-190).
   */
  firstStart: string;
  template: Template;
  templates: readonly Template[];
  meters: readonly Meter[];
  liveValues: readonly LiveValue[];
  areas: readonly SiteArea[];
  timeZone: string;
  /** Nur ansehen (HM-160): Panels zeigen, was steckt, und bieten nichts zum Ändern an. */
  readOnly: boolean;
}) {
  const client = useQueryClient();
  const ids = useId();
  const [picking, setPicking] = useState(false);
  const [adoptingIntake, setAdoptingIntake] = useState(false);
  const [panel, setPanel] = useState<Panel | null>(null);
  const [choice, setChoice] = useState<Choice | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [showMath, setShowMath] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [editingConsumers, setEditingConsumers] = useState(false);
  /**
   * **Ein Zähler, der noch keiner Wohneinheit zugeordnet ist, lässt sich
   * trotzdem in ihren Steckplatz stecken** (HM-082). Der Server verlangt die
   * Zuordnung (`422 concept.metering_point_not_in_unit`), und sie ist richtig:
   * Sie hängt an der Messstelle und überlebt den Zählerwechsel. Aber wer vor
   * dem Messkonzept steht, hat die Antwort gerade gegeben — deshalb wird sie
   * hier gefragt und in die Messstelle geschrieben, bevor das Konzept gespeichert
   * wird, statt den Nutzer zum Zähler zu schicken und zurück.
   */
  const pointsQuery = useMeteringPoints(siteId);
  const points = pointsQuery.data?.items ?? [];
  /**
   * **Ohne die Messstellen wird nicht entschieden** (HM-190). Solange sie laden,
   * hieße `unitOfMeter` für jeden Zähler „keiner Wohneinheit zugeordnet" — die
   * Rückfrage stünde falsch da oder bliebe aus. Übernehmen wartet deshalb.
   */
  const pointsReady = pointsQuery.isSuccess;
  /**
   * Die Erzeugungsanlagen hinter den Erzeugungszählern (HM-122) — im Bild als
   * Kasten unter dem Zähler. **Auch ohne gesteckten Zähler**, seit HM-125: Der
   * Kasten ist der Ort, an dem zugeordnet wird, und ein Ort, den es nur gibt,
   * wenn schon alles andere steht, findet niemand. Leer heißt dann „noch
   * nichts hinterlegt"; das Panel sagt, dass zuerst der Zähler fehlt.
   */
  const generationAssets = useGenerationAssets(siteId).data?.items ?? [];
  /**
   * **Die Zuordnung einer Erzeugungsanlage gehört dem Messkonzept** (HM-124) —
   * so wie ein Verbraucher dort hinterlegt wird, wo er hängt. Geschrieben wird
   * sie an die **Messstelle** (`generation_assets.metering_point_id`) und nicht
   * in die Fassung: Sie ändert keine Rechnung, und ein Zählerwechsel darf sie
   * nicht verlieren (ADR 0038 §1). Eine neue Fassung entsteht dafür nicht.
   */
  const [addingAsset, setAddingAsset] = useState(false);
  const assignAsset = useMutation({
    mutationFn: async (input: { assetId: string; meteringPointId: string | null }) => {
      const { error } = await api.PATCH("/generation-assets/{generationAssetId}", {
        params: { path: { generationAssetId: input.assetId } },
        body: { meteringPointId: input.meteringPointId },
      });
      if (error) throw new Error(apiErrorMessage(error) ?? "Die Zuordnung ließ sich nicht ändern.");
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["generation-assets"] });
      await client.invalidateQueries({ queryKey: ["site-energy"] });
    },
  });
  const assetsOfSlot = (slot: Slot): readonly string[] => {
    const meteringPointId = meteringPointOfSlot(template, slot, assignmentOf(concept));
    if (meteringPointId === null) return [];
    return generationAssets
      .filter((asset) => asset.meteringPointId === meteringPointId)
      .map((asset) => asset.label);
  };
  /**
   * **Ein Verbraucher lässt sich aus einem offenen Vorschlag stecken** (HM-112).
   * Der Weg über „Zähler" — Vorschlag bestätigen, zurückkommen, stecken — sind
   * zwei Rückfragen für eine Absicht, und man muss wissen, dass er dort
   * anfängt. Die Bestätigung bleibt dieselbe (`POST …/accept`); nur ihr Ort
   * ist der, an dem jemand gerade den Zähler sucht. Angelegt wird erst beim
   * Klick, nie beim Öffnen der Auswahl.
   */
  const openSuggestions = (useMeterSuggestions(siteId).data?.items ?? []).filter(
    (suggestion) => suggestion.kind === "create_meter" && suggestion.status === "pending",
  );
  const acceptSuggestion = useMutation({
    mutationFn: async (suggestionId: string): Promise<Meter> => {
      const { data, error, response } = await api.POST("/meter-suggestions/{suggestionId}/accept", {
        params: { path: { suggestionId } },
        body: {},
      });
      if (error || !data)
        throw new Error(apiErrorMessage(error) ?? `Die API antwortete mit ${response.status}.`);
      return data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["meter-suggestions"] });
      await client.invalidateQueries({ queryKey: ["meters"] });
      await client.invalidateQueries({ queryKey: ["metering-points"] });
    },
  });
  const [unitPrompt, setUnitPrompt] = useState<{
    slotKey: string;
    meter: Meter;
    to: { id: string; name: string };
    from: { id: string; name: string } | null;
    validFrom: string | null;
  } | null>(null);
  /**
   * Warum zuletzt nicht gespeichert wurde, samt dem, was danach gilt (HM-190):
   * Zuordnungen von Messstellen, die sich nicht zurücksetzen ließen, oder ein
   * Zähler, den es unter den wählbaren nicht mehr gibt. `null`, wenn nichts.
   * Steht vor den Fehlern der Mutationen — nach dem Zurückstellen trägt die
   * Zuordnungs-Mutation den Erfolg des Rückwegs, nicht den Fehler.
   */
  const [commitProblem, setCommitProblem] = useState<string | null>(null);
  const areaName = (id: string | null): string | null =>
    areas.find((area) => area.id === id)?.name ?? null;
  /** Die Wohneinheit, der die Messstelle eines Zählers heute zugeordnet ist. */
  const unitOfMeter = (meter: Meter): string | null =>
    points.find((point) => point.id === meter.meteringPointId)?.siteAreaId ?? null;

  /**
   * Wohneinheiten, die es an der Anlage gibt, aber nicht in dieser Fassung —
   * angelegt nach der Fassung. Sie kommen über „Fassung erneuern" hinein:
   * ein Layout-Delta ohne Änderung, das der Server mit den heutigen
   * Wohneinheiten expandiert.
   */
  const unitsInConcept = new Set(
    concept.slotInstances.filter((i) => i.repeat === "per_unit").map((i) => i.siteAreaId),
  );
  const missingUnits =
    template.layout?.perUnit === true ? areas.filter((area) => !unitsInConcept.has(area.id)) : [];

  const assignment = assignmentOf(concept);
  const usable = meters.filter((meter) => meter.status === "draft" || meter.status === "active");
  const meterAt = (meteringPointId: string | null): Meter | null =>
    meteringPointId === null
      ? null
      : (usable.find((meter) => meter.meteringPointId === meteringPointId) ??
        meters.find((meter) => meter.meteringPointId === meteringPointId) ??
        null);
  const meterOfSlot = (slot: Slot): Meter | null =>
    meterAt(meteringPointOfSlot(template, slot, assignment));

  const missing = template.slots.filter(
    (slot) => slot.required && meteringPointOfSlot(template, slot, assignment) === null,
  );
  const taken = new Set(Object.values(assignment).map((source) => source.meteringPointId));
  const since = conceptSince(concepts, concept);
  /**
   * **„Seit Beginn" heißt im Editor: seit Beginn dieses Konzepts** (HM-190).
   * Der Server ersetzt jede Fassung ab `validFrom`; mit dem Beginn der ältesten
   * Fassung überhaupt ersetzte eine Korrektur in Vorlage B auch die Zeit, in
   * der Vorlage A galt, und rechnete sie mit B neu.
   */
  const conceptStart = since;
  const history = conceptHistory(concepts, {
    templateLabel: (key) => templates.find((entry) => entry.key === key)?.label ?? null,
    slotLabelOfInput: (inputKey) => {
      const slotKey = template.inputs.find((input) => input.key === inputKey)?.slot;
      return template.slots.find((slot) => slot.key === slotKey)?.label ?? null;
    },
    distributionLabel,
  });
  /* Die jüngste Änderung, die gegolten hat — nicht eine, die eine Korrektur ganz ersetzt hat. */
  const latestChange = latestEffectiveChange(history);
  const layout = concept.layout ?? DEFAULT_LAYOUT;
  /* Weicht ein Erzeugungszähler heute von der Fassung ab (#42, #43)? Dann bietet die Seite an,
     das ab einem Zeitpunkt zu übernehmen. */
  const intakeChanged = intakeChange(
    concept.intakeSlots,
    template.slots.filter((slot) => isGenerationSlot(slot)),
  );
  const intakeTexts = intakeChanged === null ? null : intakeChangeTexts(intakeChanged);
  const distributions: readonly PvDistribution[] = template.layout?.distributions ?? [];

  const closePanel = () => {
    setPanel(null);
    setChoice(null);
    setCommitProblem(null);
    setUnitPrompt(null);
    setEditingConsumers(false);
    setAddingAsset(false);
  };

  /**
   * **Die Rollen kommen aus derselben Rechnung wie die Skizze**
   * (`conceptStructure`): „Wohnungszähler" ist eine Zeile mit allen
   * Wohnungszählern, in der Skizze ein Strang mit einem Kasten je Zähler.
   * Verbraucher eines Hauses hängen hinter „Verbrauch im Haus", Verbraucher
   * einer Wohneinheit hinter ihrem Zähler — beides sind keine Rollen, sondern
   * Aufschlüsselungen, und sie werden dort hinterlegt, wo sie stehen.
   */
  const structure = conceptStructure(template);
  const houseConsumers = structure.houseConsumers;
  const behindHouse = consumersBehindHouse(template);
  const houseGroup: GroupRef = {
    key: HOUSE_KEY,
    label: "Verbrauch im Haus",
    slots: houseConsumers.map((slot) => slot.key),
    kind: "house",
    siteAreaId: null,
  };
  const behindGroup = (unitSlotKey: string): GroupRef | null => {
    const unit = template.slots.find((slot) => slot.key === unitSlotKey);
    if (unit === undefined) return null;
    return {
      key: behindKey(unit.key),
      label: unit.label,
      slots: (structure.behindUnit.get(unit.key) ?? []).map((slot) => slot.key),
      kind: "behind",
      siteAreaId: unit.siteAreaId,
    };
  };

  const save = useMutation({
    mutationFn: async (input: {
      inputs?: Assignment;
      layout?: Layout;
      adoptIntake?: boolean;
      validFrom?: string;
    }) => {
      const { data, error, response } = await api.PATCH("/concepts/{conceptId}", {
        params: { path: { conceptId: concept.id } },
        body: input,
      });
      if (error || !data)
        throw new Error(apiErrorMessage(error) ?? `Die API antwortete mit ${response.status}.`);
      return data;
    },
    onSuccess: async () => {
      closePanel();
      await client.invalidateQueries({ queryKey: ["concepts", siteId] });
      await client.invalidateQueries({ queryKey: ["site-energy", siteId] });
    },
  });

  /** Die neue Zuordnung (`lib/concept-editor.ts`): Rollen nur, die der Zähler führt. */
  const assignmentWith = (
    slotKey: string,
    meter: Meter | null,
    roles: Readonly<Record<string, Role>>,
  ): Assignment => assignmentWithFor(template, assignment, slotKey, meter, roles);

  const assignUnit = useMutation({
    mutationFn: async (input: { meteringPointId: string; siteAreaId: string | null }) => {
      const { error, response } = await api.PATCH("/metering-points/{meteringPointId}", {
        params: { path: { meteringPointId: input.meteringPointId } },
        body: { siteAreaId: input.siteAreaId },
      });
      if (error)
        throw new Error(apiErrorMessage(error) ?? `Die API antwortete mit ${response.status}.`);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["metering-points", siteId] });
      await client.invalidateQueries({ queryKey: ["site-areas", siteId] });
    },
  });

  /**
   * Zuordnen, speichern — und bei einem Fehler zurückstellen
   * (`lib/unit-moves.ts`). Ohne Zuordnung ist es nur das Speichern.
   */
  const saveWithMoves = async (
    moves: readonly UnitMove[],
    body: { inputs?: Assignment; layout?: Layout; validFrom?: string },
  ): Promise<void> => {
    setCommitProblem(null);
    if (moves.length === 0) {
      save.mutate(body);
      return;
    }
    const outcome = await saveWithUnitMoves(
      moves,
      async (meteringPointId, siteAreaId) => {
        await assignUnit.mutateAsync({ meteringPointId, siteAreaId });
      },
      async () => {
        await save.mutateAsync(body);
      },
    );
    if (outcome.status === "failed") {
      const reason =
        outcome.error instanceof Error
          ? outcome.error.message
          : "Die Änderung ließ sich nicht speichern.";
      const stuck = stuckMovesMessage(outcome.stuck, (id) => areaName(id) ?? "Wohneinheit");
      setCommitProblem(
        stuck !== null
          ? `${reason} ${stuck}`
          : outcome.restored.length > 0
            ? `${reason} Die Zuordnung der Messstelle ist zurückgesetzt.`
            : reason,
      );
      await client.invalidateQueries({ queryKey: ["metering-points", siteId] });
    }
  };

  /** `validFrom` als ISO-Zeitpunkt — `null` heißt „ab jetzt". */
  const commit = (validFrom: string | null): void => {
    if (choice === null || !pointsReady) return;
    /* „Nicht gefunden" heißt nie „leeren" (HM-190): Ein Zähler, der in der
       Wahl steht und unter den wählbaren fehlt, räumte sonst den Steckplatz aus. */
    const resolved = resolveChosenMeter(choice.meterId, usable);
    if (!resolved.ok) {
      setCommitProblem(
        "Der gewählte Zähler steht nicht mehr zur Wahl — die Zuordnung bleibt, wie sie ist.",
      );
      return;
    }
    setCommitProblem(null);
    const meter = resolved.meter;
    const slot = template.slots.find((entry) => entry.key === choice.slot);
    const targetUnit = slot?.siteAreaId ?? null;
    if (meter !== null && targetUnit !== null && unitOfMeter(meter) !== targetUnit) {
      const fromId = unitOfMeter(meter);
      setUnitPrompt({
        slotKey: choice.slot,
        meter,
        to: { id: targetUnit, name: areaName(targetUnit) ?? slot?.label ?? "Wohneinheit" },
        from:
          fromId === null ? null : { id: fromId, name: areaName(fromId) ?? "andere Wohneinheit" },
        validFrom,
      });
      return;
    }
    const inputs = assignmentWith(choice.slot, meter, choice.roles);
    /* Ob die Aufnahme mitrechnet, leitet der Server aus dem neuen Zähler ab (#42). */
    save.mutate({
      inputs,
      ...(validFrom === null ? {} : { validFrom }),
    });
  };

  /**
   * Erst die Messstelle der Wohneinheit zuordnen, dann das Konzept speichern —
   * und scheitert das Speichern, die Zuordnung zurückstellen (HM-190).
   */
  const confirmUnit = async (): Promise<void> => {
    if (unitPrompt === null || choice === null) return;
    const prompt = unitPrompt;
    setUnitPrompt(null);
    await saveWithMoves(
      [
        {
          meteringPointId: prompt.meter.meteringPointId,
          meterLabel: prompt.meter.label,
          to: prompt.to.id,
          from: prompt.from?.id ?? null,
        },
      ],
      {
        inputs: assignmentWith(choice.slot, prompt.meter, choice.roles),
        ...(prompt.validFrom === null ? {} : { validFrom: prompt.validFrom }),
      },
    );
  };

  /** Die Rollen, die im Konzept für die Eingänge eines Steckplatzes gerade gelten. */
  const rolesOfSlot = (slotKey: string): Record<string, Role> =>
    Object.fromEntries(
      template.inputs
        .filter((input) => input.slot === slotKey && assignment[input.key] !== undefined)
        .map((input) => [input.key, assignment[input.key]!.role]),
    );

  /** Einen Steckplatz öffnen: leer in die Wahl, belegt in die Ansicht. Ein zweiter Klick schließt. */
  const openSlot = (from: PanelFrom, slotKey: string, group: GroupRef | null = null): void => {
    const slot = template.slots.find((entry) => entry.key === slotKey);
    if (slot === undefined) return;
    if (
      panel !== null &&
      panel.from === from &&
      (panel.mode === "info" || panel.mode === "choose" || panel.mode === "unassign") &&
      panel.slot === slotKey
    ) {
      closePanel();
      return;
    }
    save.reset();
    setEditingConsumers(false);
    setUnitPrompt(null);
    const meter = meterOfSlot(slot);
    /* Ein leerer Steckplatz hat nur die Wahl — und die gibt es ohne Schreibrecht nicht. */
    if (meter === null && readOnly) return;
    if (meter === null) {
      setPanel({ from, mode: "choose", slot: slotKey, group });
      setChoice({ slot: slotKey, meterId: null, roles: {} });
    } else {
      setPanel({ from, mode: "info", slot: slotKey, group });
      setChoice(null);
    }
  };

  const openGroup = (from: PanelFrom, group: GroupRef): void => {
    if (
      panel !== null &&
      panel.from === from &&
      panel.mode === "group" &&
      panel.key === group.key
    ) {
      closePanel();
      return;
    }
    closePanel();
    setPanel({ from, mode: "group", ...group });
  };
  const openHouse = (from: PanelFrom): void => openGroup(from, houseGroup);
  const openAssets = (from: PanelFrom, slotKey: string): void => {
    setChoice(null);
    setEditingConsumers(false);
    setPanel({ from, mode: "assets", slot: slotKey });
  };
  const openAdd = (from: PanelFrom, roleKey: string): void => {
    if (readOnly) return;
    if (panel !== null && panel.from === from && panel.mode === "add" && panel.role === roleKey) {
      closePanel();
      return;
    }
    closePanel();
    setPanel({ from, mode: "add", key: addKey(roleKey), role: roleKey });
  };
  /** Aus der Liste: ein Steckplatz öffnet ihn, mehrere ihre Aufzählung, keiner das Hinzufügen. */
  const openRole = (role: ConceptRole): void => {
    const [only] = role.slots;
    if (role.slots.length === 0) openAdd("list", role.key);
    else if (role.slots.length === 1 && only !== undefined) openSlot("list", only.key);
    else
      openGroup("list", {
        key: role.key,
        label: role.label,
        slots: role.slots.map((slot) => slot.key),
        kind: "group",
        siteAreaId: null,
      });
  };

  const onSketchPick = (pick: SketchPick): void => {
    switch (pick.kind) {
      case "slot":
        openSlot("schema", pick.slot);
        return;
      case "house":
        openHouse("schema");
        return;
      case "behind": {
        const group = behindGroup(pick.unitSlot);
        if (group !== null) openGroup("schema", group);
        return;
      }
      case "add":
        openAdd("schema", pick.role);
        return;
      case "assets":
        openAssets("schema", pick.slot);
        return;
    }
  };

  /** Der Schlüssel, den die Skizze hervorhebt — die Gruppe, wenn über sie gewählt wurde. */
  const sketchKey =
    panel === null || panel.from !== "schema"
      ? null
      : panel.mode === "group" || panel.mode === "add"
        ? panel.key
        : /* Der Kasten „Erzeugungsanlagen" ist unter seinem Steckplatz gewählt (HM-124). */
          panel.mode === "assets"
          ? assetsKey(panel.slot)
          : panel.group?.kind === "group"
            ? panel.slot
            : (panel.group?.key ?? panel.slot);

  /** Der Steckplatz eines Verbrauchers dieser Fassung — über die Instanzen, nicht über einen gebildeten Schlüssel (der ist opak). */
  const slotOfConsumer = (consumerKey: string): string | null =>
    concept.slotInstances.find((i) => i.consumerKey === consumerKey)?.slotKey ?? null;

  /** Die Verbraucher- und Erzeugerbearbeitung, eingebettet dort, wo sie gebraucht wird. */
  const layoutEditor = (draft: (current: Layout) => Layout, focus?: "consumers" | "generators") => (
    <LayoutEditor
      embedded={{ onClose: closePanel, draft, ...(focus === undefined ? {} : { focus }) }}
      template={template}
      layout={layout}
      conceptStart={conceptStart}
      latestChange={concept.validFrom}
      timeZone={timeZone}
      units={template.layout?.consumerUnits === true ? areas : []}
      slotOfConsumer={slotOfConsumer}
      meterOfSlotKey={(slotKey) => {
        const slot = template.slots.find((entry) => entry.key === slotKey);
        return slot === undefined ? null : meterOfSlot(slot);
      }}
      unitOfMeter={unitOfMeter}
      busy={save.isPending || assignUnit.isPending || !pointsReady}
      error={
        commitProblem ??
        (save.isError ? save.error.message : assignUnit.isError ? assignUnit.error.message : null)
      }
      onCommit={(next, validFrom, moves) =>
        void saveWithMoves(moves, { layout: next, ...(validFrom === null ? {} : { validFrom }) })
      }
    />
  );
  const withNewConsumer =
    (siteAreaId: string | null) =>
    (current: Layout): Layout => ({
      ...current,
      consumers: [
        ...current.consumers,
        {
          kind: behindHouse || siteAreaId !== null ? "wallbox" : "heat_pump",
          label: "",
          position: null,
          siteAreaId,
        },
      ],
    });

  const errorText =
    commitProblem ??
    (save.isError
      ? save.error.message
      : assignUnit.isError
        ? assignUnit.error.message
        : acceptSuggestion.isError
          ? acceptSuggestion.error.message
          : assignAsset.isError
            ? assignAsset.error.message
            : pointsQuery.isError
              ? "Die Zuordnung der Zähler zu den Wohneinheiten ließ sich nicht laden; Übernehmen geht erst danach."
              : null);

  const renderPanel = (from: PanelFrom, padding: string) => {
    if (panel === null || panel.from !== from) return null;

    /**
     * **Die Erzeugungsanlagen hinter einem Erzeugungszähler** (HM-124) — wie
     * die Verbraucher: eine Aufschlüsselung, dort hinterlegt, wo sie im Bild
     * steht. Ohne Zähler im Steckplatz gibt es keine Messstelle, hinter der
     * etwas stehen könnte; dann sagt das Panel das, statt eine Auswahl
     * anzubieten, die ins Leere liefe (Regel 10).
     */
    if (panel.mode === "assets") {
      const slot = template.slots.find((entry) => entry.key === panel.slot);
      if (slot === undefined) return null;
      const meteringPointId = meteringPointOfSlot(template, slot, assignment);
      const behind = generationAssets.filter(
        (asset) => meteringPointId !== null && asset.meteringPointId === meteringPointId,
      );
      /* Hinter einem Erzeugungszähler PV-Anlagen und Speicher (#40). */
      const kinds = assetKindsOf(slot);
      const others = generationAssets.filter(
        (asset) => !behind.includes(asset) && kinds.includes(asset.kind),
      );
      const meterName = (asset: (typeof generationAssets)[number]): string | null =>
        meterAt(asset.meteringPointId)?.label ?? null;

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, padding }}>
          <span style={{ fontSize: 11.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
            Erzeugungsanlagen hinter{" "}
            <strong style={{ color: "var(--text-body)" }}>{slot.label}</strong> — die PV-Anlagen und
            Speicher, die dieser Zähler misst. Sie schlüsseln auf und ändern die Bilanz nicht; aus
            ihrer Nennleistung entsteht der Ertrag je kWp, ein Speicher zeigt Ladung, Entladung und
            Ladezustand.
          </span>

          {meteringPointId === null ? (
            <span style={{ ...MUTED, padding: "2px 0" }}>
              Erst den Zähler wählen: Ohne ihn gibt es keine Messstelle, hinter der eine Anlage
              stehen könnte.
            </span>
          ) : (
            <>
              {behind.map((asset) => (
                <div
                  key={asset.id}
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "var(--sp-3)",
                    alignItems: "center",
                    padding: "9px 12px",
                    borderRadius: "var(--radius-sm)",
                    background: "var(--surface-inset)",
                  }}
                >
                  <span
                    style={{ display: "inline-flex", flexShrink: 0, color: "var(--text-muted)" }}
                  >
                    {asset.kind === "pv" ? (
                      <icons.IconSun size={16} />
                    ) : (
                      <icons.IconBolt size={16} />
                    )}
                  </span>
                  <span style={{ minWidth: 0, flex: "1 1 180px" }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-strong)",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {asset.label}
                    </span>
                    <span style={{ display: "block", fontSize: 11.5, color: "var(--text-muted)" }}>
                      {[kindLabel(asset.kind), ratingText(asset), orientationText(asset)]
                        .filter((part) => part.length > 0)
                        .join(" · ")}
                    </span>
                  </span>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={assignAsset.isPending}
                      onClick={() =>
                        assignAsset.mutate({ assetId: asset.id, meteringPointId: null })
                      }
                    >
                      Lösen
                    </Button>
                  )}
                </div>
              ))}

              {behind.length === 0 && (
                <span style={{ ...MUTED, padding: "2px 0" }}>
                  Keine Erzeugungsanlage hinterlegt — der Zähler misst dann eine Erzeugung, von der
                  niemand weiß, woher sie kommt.
                </span>
              )}

              {others.length > 0 && !readOnly && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingTop: 2 }}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "var(--tracking-wide)",
                      textTransform: "uppercase",
                      color: "var(--text-faint)",
                    }}
                  >
                    Zuordnen
                  </span>
                  {others.map((asset) => {
                    const at = meterName(asset);
                    return (
                      <div
                        key={asset.id}
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "var(--sp-2)",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            minWidth: 0,
                            flex: "1 1 180px",
                            fontSize: 12.5,
                            color: "var(--text-body)",
                          }}
                        >
                          {asset.label}
                          <span style={{ color: "var(--text-faint)" }}>
                            {" · "}
                            {kindLabel(asset.kind)}
                            {at === null ? " · noch keinem Zähler" : ` · heute hinter ${at}`}
                          </span>
                        </span>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={assignAsset.isPending}
                          onClick={() => assignAsset.mutate({ assetId: asset.id, meteringPointId })}
                        >
                          {at === null ? "Zuordnen" : "Hierher"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {errorText !== null && <Alert tone="danger">{errorText}</Alert>}

          <div style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap", paddingTop: 2 }}>
            {meteringPointId !== null && !readOnly && (
              <Button variant="secondary" size="sm" onClick={() => setAddingAsset(true)}>
                <icons.IconPlus size={14} />
                Erzeugungsanlage anlegen …
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={closePanel}>
              Schließen
            </Button>
          </div>

          {addingAsset && meteringPointId !== null && !readOnly && (
            <GenerationAssetDialog
              siteId={siteId}
              asset={null}
              meteringPointId={meteringPointId}
              initialKind={kinds[0] ?? "pv"}
              onClose={() => setAddingAsset(false)}
            />
          )}
        </div>
      );
    }

    if (panel.mode === "group" && (panel.kind === "house" || panel.kind === "behind")) {
      const group: GroupRef = panel;
      const consumers = template.slots.filter((slot) => group.slots.includes(slot.key));
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, padding }}>
          <span style={{ fontSize: 11.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
            {group.kind === "house" ? (
              <>
                Verbraucher hinter „Verbrauch im Haus" — was im Haus einen eigenen Zähler hat. Sie
                schlüsseln den Verbrauch auf und ändern die Bilanz nicht; was sie nicht zählen,
                führt das Konzept als übrigen Verbrauch.
              </>
            ) : (
              <>
                Hinter <strong style={{ color: "var(--text-body)" }}>{group.label}</strong> — etwa
                die Wallbox dieser Wohnung. Schlüsselt auf, was ihr Zähler misst; ändert die Bilanz
                nicht.
              </>
            )}
          </span>
          {!editingConsumers &&
            consumers.map((slot) => {
              const meter = meterOfSlot(slot);
              const kind = slot.consumer?.kind ?? "other";
              return (
                <div
                  key={slot.key}
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "var(--sp-3)",
                    alignItems: "center",
                    padding: "9px 12px",
                    borderRadius: "var(--radius-sm)",
                    background: "var(--surface-inset)",
                  }}
                >
                  <span
                    style={{ display: "inline-flex", flexShrink: 0, color: "var(--text-muted)" }}
                  >
                    {kind === "wallbox" ? (
                      <icons.IconPlug size={16} />
                    ) : (
                      <icons.IconBolt size={16} />
                    )}
                  </span>
                  <span style={{ minWidth: 0, flex: "1 1 180px" }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-strong)",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {slot.label}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 11.5,
                        color: meter === null ? "var(--hm-warning)" : "var(--text-muted)",
                      }}
                    >
                      {t(`consumer.kind.${kind}`, "de-DE")} ·{" "}
                      {meter === null
                        ? "kein Zähler — sein Verbrauch fehlt"
                        : `Zähler: ${meter.label}`}
                    </span>
                  </span>
                  {(meter !== null || !readOnly) && (
                    <Button
                      variant={meter === null ? "primary" : "ghost"}
                      size="sm"
                      onClick={() => openSlot(from, slot.key, group)}
                    >
                      {meter === null ? "Zähler wählen" : "Ansehen"}
                    </Button>
                  )}
                </div>
              );
            })}
          {!editingConsumers && consumers.length === 0 && (
            <span style={{ ...MUTED, padding: "2px 0" }}>
              {group.kind === "house"
                ? "Kein Verbraucher hinterlegt — der Verbrauch des Hauses steht ungeteilt am Hausanschluss."
                : "Kein Verbraucher hinterlegt."}
            </span>
          )}
          {editingConsumers && !readOnly ? (
            layoutEditor(withNewConsumer(group.siteAreaId), "consumers")
          ) : (
            <div style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap", paddingTop: 2 }}>
              {!readOnly && (
                <Button variant="secondary" size="sm" onClick={() => setEditingConsumers(true)}>
                  <icons.IconPlus size={14} />
                  {consumers.length === 0 ? "Verbraucher hinterlegen …" : "Verbraucher ergänzen …"}
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={closePanel}>
                Schließen
              </Button>
            </div>
          )}
        </div>
      );
    }

    if (panel.mode === "add") {
      const role = structure.roles.find((entry) => entry.key === panel.role);
      if (role === undefined || role.add === null) return null;
      if (role.add === "units") {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding }}>
            <span style={MUTED}>
              Je Wohneinheit der Anlage gibt es einen Wohnungszähler. Eine neue Wohneinheit legst du
              unter „Wohneinheiten" an; „Wohneinheiten übernehmen" nimmt sie danach ins Messkonzept
              auf.
            </span>
            {missingUnits.length > 0 && (
              <Alert
                tone="info"
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => save.mutate({ layout })}
                  >
                    Wohneinheiten übernehmen
                  </Button>
                }
              >
                {missingUnits.length === 1
                  ? `Die Wohneinheit „${missingUnits[0]?.name}" ist angelegt, aber noch nicht im Messkonzept.`
                  : `${missingUnits.length} Wohneinheiten sind angelegt, aber noch nicht im Messkonzept.`}
              </Alert>
            )}
            <div style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate({ kind: "site", siteId, area: "units" })}
              >
                Zu den Wohneinheiten
              </Button>
              <Button variant="ghost" size="sm" onClick={closePanel}>
                Schließen
              </Button>
            </div>
          </div>
        );
      }
      const max = template.layout?.generators.max ?? 1;
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding }}>
          <span style={MUTED}>
            {role.add === "consumers"
              ? "Jeder weitere Verbraucher bekommt einen eigenen Steckplatz — Allgemeinstrom, Wärmepumpe, Wallbox. Den Zähler wählst du danach im Bild."
              : "Jeder weitere Erzeugungszähler bekommt einen eigenen Steckplatz; gerechnet wird mit ihrer Summe."}
          </span>
          {role.add === "consumers"
            ? layoutEditor(withNewConsumer(null), "consumers")
            : layoutEditor(
                (current) => ({
                  ...current,
                  generatorCount: Math.min(current.generatorCount + 1, max),
                }),
                "generators",
              )}
        </div>
      );
    }

    if (panel.mode === "group") {
      const slots = template.slots.filter((slot) => panel.slots.includes(slot.key));
      const group: GroupRef = panel;
      const role = structure.roles.find((entry) => entry.key === panel.key);
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, padding }}>
          <span style={{ fontSize: 11.5, color: "var(--text-muted)", paddingBottom: 4 }}>
            {panel.label} — {slots.filter((slot) => meterOfSlot(slot) !== null).length} von{" "}
            {slots.length} zugeordnet
          </span>
          {slots.map((slot) => {
            const meter = meterOfSlot(slot);
            return (
              <div
                key={slot.key}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "var(--sp-2)",
                  alignItems: "center",
                  padding: "8px 12px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border-subtle)",
                }}
              >
                <span
                  style={{
                    minWidth: 0,
                    flex: "1 1 160px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--text-strong)",
                  }}
                >
                  {slot.label}
                </span>
                <span
                  style={{
                    minWidth: 0,
                    flex: "1 1 160px",
                    fontSize: 12.5,
                    color:
                      meter !== null
                        ? "var(--text-body)"
                        : slot.required
                          ? "var(--hm-warning)"
                          : "var(--text-muted)",
                  }}
                >
                  {meter?.label ?? (slot.required ? "Pflicht · kein Zähler" : "optional · leer")}
                </span>
                {(meter !== null || !readOnly) && (
                  <Button variant="ghost" size="sm" onClick={() => openSlot(from, slot.key, group)}>
                    {meter === null ? "Zähler wählen" : "Ansehen"}
                  </Button>
                )}
              </div>
            );
          })}
          <div style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap", paddingTop: 6 }}>
            {role?.add != null && !readOnly && (
              <Button variant="secondary" size="sm" onClick={() => openAdd(from, role.key)}>
                <icons.IconPlus size={14} />
                Weiteren Zähler hinzufügen
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={closePanel}>
              Fertig
            </Button>
          </div>
        </div>
      );
    }

    const slot = template.slots.find((entry) => entry.key === panel.slot);
    if (slot === undefined) return null;
    const meter = meterOfSlot(slot);
    const back = () => {
      closePanel();
      if (panel.group !== null) setPanel({ from, mode: "group", ...panel.group });
    };
    /* Die Aufnahme gibt es nur am Erzeugungszähler einer Vorlage, die sie kennt (#40); ob sie
       mitrechnet, folgt aus dem Zähler (#42) — hier steht es nur. */
    const intakeOffered = template.layout?.generationIntake === true && isGenerationSlot(slot);
    /* Je Erzeugungszähler (#43): ob die Fassung seine Aufnahme zählt und ob er es könnte. Eine
       abgelöste Fassung hat keine `intakeSlots` — dann sagt ihr Graph, was sie zählte, und ob
       der Zähler es heute könnte, steht dort nicht zur Frage. */
    const intakeOfSlot = concept.intakeSlots?.find((entry) => entry.slotKey === slot.key) ?? null;
    const intakeCounted =
      intakeOfSlot?.counted ??
      concept.nodes.some(
        (node) =>
          isIntakeInput(node.key) &&
          node.meteringPointId !== null &&
          node.meteringPointId === meteringPointOfSlot(template, slot, assignment),
      );

    if (panel.mode === "info" && meter !== null) {
      const roleLines = template.inputs.filter(
        (input) =>
          input.slot === slot.key && input.roles.length > 1 && assignment[input.key] !== undefined,
      );
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, padding }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "var(--sp-3)",
              alignItems: "center",
              padding: "11px 13px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            <MeterChip />
            <span style={{ minWidth: 0, flex: "1 1 200px" }}>
              <span
                style={{
                  display: "block",
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: "var(--text-strong)",
                }}
              >
                {meter.label}
              </span>
              <span
                style={{ ...MONO, display: "block", fontSize: 11.5, color: "var(--text-muted)" }}
              >
                {meterNumberText(meter)} · {meterKindLabel(meter.kind)}
              </span>
            </span>
            {meter.status !== "draft" && meter.status !== "active" && (
              <Badge tone="warning">
                {meter.status === "archived" ? "archiviert" : "abgelöst"}
              </Badge>
            )}
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {slot.label} · liefert {slot.delivers.join(", ")}
            </span>
          </div>
          {roleLines.map((input) => (
            <span key={input.key} style={{ fontSize: 12.5, color: "var(--text-body)" }}>
              „{input.label}" zählt aus dem Zählwerk{" "}
              <strong>{registerRoleLabel(assignment[input.key]!.role)}</strong>
            </span>
          ))}
          {intakeOffered && (
            <span style={{ fontSize: 12.5, color: "var(--text-body)" }}>
              {intakeCounted
                ? "Zählt auch, was in die Anlagen hineingeht — etwa ein Speicher, der lädt."
                : concept.intakeSlots == null
                  ? "Zählt in dieser Fassung nur, was aus den Anlagen herausgeht."
                  : intakeOfSlot?.available === true
                    ? "Zählt in dieser Fassung nur, was herausgeht — der Zähler kann inzwischen auch die Gegenrichtung; übernehmen lässt sie sich oben."
                    : "Zählt nur, was aus den Anlagen herausgeht — der Zähler führt kein Zählwerk in der Gegenrichtung."}
            </span>
          )}
          {!readOnly && (
            <span style={MUTED}>
              Ein Wechsel oder das Aufheben der Zuordnung gilt ab einem Zeitpunkt und verändert von
              da an die Berechnung. Beides muss darum bestätigt werden.
            </span>
          )}
          <div style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
            {!readOnly && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setPanel({ ...panel, mode: "choose" });
                    setChoice({ slot: slot.key, meterId: meter.id, roles: rolesOfSlot(slot.key) });
                  }}
                >
                  Zähler wechseln
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPanel({ ...panel, mode: "unassign" });
                    setChoice({ slot: slot.key, meterId: null, roles: {} });
                  }}
                >
                  Zuordnung aufheben
                </Button>
              </>
            )}
            {/* **Der Weg zu den Erzeugungsanlagen ohne die Skizze** (HM-124):
                Im Bild ist es der Kasten darunter, hier der Knopf — die Liste
                ist der Ausweg für die Tastatur. */}
            {isAssetSlot(slot) && (
              <Button variant="ghost" size="sm" onClick={() => openAssets(from, slot.key)}>
                Erzeugungsanlagen …
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={back}>
              {panel.group === null ? "Schließen" : "Zurück"}
            </Button>
          </div>
        </div>
      );
    }

    if (panel.mode === "unassign" && meter !== null && !readOnly) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding }}>
          <ValidFromChoice
            question={`Zuordnung von „${meter.label}" aufheben — ab wann?`}
            note={`Ab diesem Zeitpunkt liefert der Steckplatz ${slot.label} keine Werte mehr; was von ihm abhängt, ist dann eine Lücke. Bis dahin bleibt die bisherige Zuordnung in der Rechnung.`}
            acknowledge="Mir ist bewusst, dass dieser Steckplatz ab dem Zeitpunkt keine Werte mehr liefert."
            submitLabel="Zuordnung aufheben"
            conceptStart={conceptStart}
            latestChange={concept.validFrom}
            timeZone={timeZone}
            busy={save.isPending}
            onCommit={commit}
            onCancel={() => {
              setPanel({ ...panel, mode: "info" });
              setChoice(null);
            }}
            cancelLabel="Zurück"
          />
          {errorText !== null && <Alert tone="danger">{errorText}</Alert>}
        </div>
      );
    }

    if (choice === null || readOnly) return null;
    return (
      <>
        <MeterChooser
          slot={slot}
          template={template}
          meters={usable}
          current={meter?.id ?? null}
          currentLabel={meter?.label ?? null}
          chosen={choice.meterId}
          chosenRoles={choice.roles}
          currentRoles={rolesOfSlot(slot.key)}
          liveValues={liveValues}
          taken={taken}
          busy={
            save.isPending || acceptSuggestion.isPending || assignUnit.isPending || !pointsReady
          }
          conceptStart={conceptStart}
          latestChange={concept.validFrom}
          timeZone={timeZone}
          padding={padding}
          suggestions={
            slot.repeat === "per_consumer"
              ? openSuggestions.filter((s) => suggestionFitsSlot(s, template, slot))
              : []
          }
          onAcceptSuggestion={async (suggestionId) => {
            setUnitPrompt(null);
            try {
              const created = await acceptSuggestion.mutateAsync(suggestionId);
              /* Ein anderer Zähler, also keine Rolle vom vorigen (HM-190). */
              setChoice({ slot: slot.key, meterId: created.id, roles: {} });
            } catch {
              // Der Fehler steht an der Mutation; die Auswahl bleibt offen.
            }
          }}
          onChoose={(meterId) => {
            setUnitPrompt(null);
            setCommitProblem(null);
            /* **Eine gewählte Rolle gehört zu ihrem Zähler** (HM-190). Beim Wechsel
               auf einen anderen fängt die Wahl neu an — sonst ginge die
               Einspeisung des vorigen Zweirichtungszählers an einen Zähler, der
               keine führt, und die Auswahl dafür stünde nicht einmal da. Zurück
               auf den gesteckten gilt wieder, was im Konzept steht. */
            setChoice({
              slot: slot.key,
              meterId,
              roles:
                meterId === choice.meterId
                  ? choice.roles
                  : meterId === meter?.id
                    ? rolesOfSlot(slot.key)
                    : {},
            });
          }}
          onRole={(inputKey, role) =>
            setChoice({ ...choice, roles: { ...choice.roles, [inputKey]: role } })
          }
          onCommit={commit}
          cancelLabel={meter === null ? (panel.group === null ? "Abbrechen" : "Zurück") : "Zurück"}
          onCancel={() => {
            if (meter !== null) {
              setPanel({ ...panel, mode: "info" });
              setChoice(null);
              setUnitPrompt(null);
            } else {
              back();
            }
          }}
          error={errorText}
          unitNote={(candidate) => {
            const unit = unitOfMeter(candidate);
            if (unit === slot.siteAreaId) return null;
            return unit === null
              ? "keiner Wohneinheit zugeordnet"
              : `zugeordnet: ${areaName(unit) ?? "andere Wohneinheit"}`;
          }}
        />
        {unitPrompt !== null && unitPrompt.slotKey === slot.key && (
          <div style={{ padding: from === "list" ? "0 18px 16px" : "0 0 8px" }}>
            <Alert
              tone="info"
              action={
                <Button
                  size="sm"
                  disabled={assignUnit.isPending || save.isPending || !pointsReady}
                  onClick={() => void confirmUnit()}
                >
                  {unitPrompt.from === null ? "Zuordnen und übernehmen" : "Umziehen und übernehmen"}
                </Button>
              }
            >
              <strong>
                {unitPrompt.from === null
                  ? `Der Zähler „${unitPrompt.meter.label}" ist noch keiner Wohneinheit zugeordnet.`
                  : `Der Zähler „${unitPrompt.meter.label}" ist der Wohneinheit „${unitPrompt.from.name}" zugeordnet.`}
              </strong>{" "}
              Soll seine Messstelle der Wohneinheit <strong>„{unitPrompt.to.name}"</strong>{" "}
              zugeordnet werden? Die Zuordnung gilt für die Messstelle und bleibt bei einem
              Zählerwechsel bestehen. Wer das nicht will, wählt einen anderen Zähler oder bricht ab.
            </Alert>
          </div>
        )}
      </>
    );
  };

  /* Nach Pflicht geteilt: Nur eine Rolle mit leerem PFLICHTsteckplatz ist
     etwas zu tun. Eine leere optionale ist ein gültiger Endzustand — stünde sie
     unter „Noch zu tun", während der Kopf „Vollständig" meldet, widersprächen
     sich zwei Anzeigen zum selben Zustand. */
  const openSlotsOf = (role: ConceptRole) =>
    role.slots.filter((slot) => slot.required && meterOfSlot(slot) === null);
  const todoRoles = structure.roles.filter((role) => openSlotsOf(role).length > 0);
  const doneRoles = structure.roles.filter(
    (role) => !todoRoles.includes(role) && role.slots.some((slot) => meterOfSlot(slot) !== null),
  );
  const freeRoles = structure.roles.filter(
    (role) => !todoRoles.includes(role) && !doneRoles.includes(role),
  );
  const restRoles = [...doneRoles, ...freeRoles];
  const houseMissing = houseConsumers.filter((slot) => meterOfSlot(slot) === null);

  /** Zu welcher Rolle ein Verbraucher-Kasten gehört — die selbst genutzte Einheit oder die Wohnungszähler. */
  const roleOfBehind = (groupKey: string): string | null =>
    structure.roles.find(
      (role) =>
        `${role.key}:dahinter` === groupKey ||
        role.slots.some((slot) => behindKey(slot.key) === groupKey),
    )?.key ?? null;

  /** Unter welcher Zeile der Liste ein offenes Panel steht. */
  const listRow: string | null = (() => {
    if (panel === null || panel.from !== "list") return null;
    if (panel.mode === "add") return panel.role;
    if (panel.mode === "group")
      return panel.kind === "house"
        ? HOUSE_KEY
        : panel.kind === "behind"
          ? roleOfBehind(panel.key)
          : panel.key;
    if (panel.mode === "assets") {
      return (
        structure.roles.find((role) => role.slots.some((slot) => slot.key === panel.slot))?.key ??
        null
      );
    }
    if (panel.group !== null)
      return panel.group.kind === "house"
        ? HOUSE_KEY
        : panel.group.kind === "behind"
          ? roleOfBehind(panel.group.key)
          : panel.group.key;
    return (
      structure.roles.find((role) => role.slots.some((slot) => slot.key === panel.slot))?.key ??
      null
    );
  })();

  /** Das Symbol einer Rolle — Wegweisung, keine Datenaussage (ADR 0013), deshalb ohne Größenfarbe. */
  const roleIcon = (role: ConceptRole) =>
    role.key === structure.connection?.key ? (
      <icons.IconBolt size={15} />
    ) : role.family === "generators" || role.up ? (
      <icons.IconSun size={15} />
    ) : role.family === "consumers" ? (
      <icons.IconPlug size={15} />
    ) : (
      <icons.IconGauge size={15} />
    );

  const roleRow = (role: ConceptRole) => {
    const selected = listRow === role.key;
    const assigned = role.slots.flatMap((slot) => {
      const meter = meterOfSlot(slot);
      return meter === null ? [] : [meter];
    });
    const open = openSlotsOf(role);
    const ownSlot = role.family === "own_unit" ? role.slots[0] : undefined;
    const behind =
      role.family === "units"
        ? role.slots.flatMap((slot) => structure.behindUnit.get(slot.key) ?? [])
        : ownSlot !== undefined
          ? (structure.behindUnit.get(ownSlot.key) ?? [])
          : [];
    const hint = [role.hint, `liefert ${role.delivers.join(", ")}`]
      .filter((part) => part.length > 0)
      .join(" — ");
    return (
      <div key={role.key} style={{ borderTop: "1px solid var(--border-subtle)" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-3)",
            alignItems: "center",
            padding: "11px 18px",
            background: selected ? "var(--surface-inset)" : "transparent",
          }}
        >
          <span
            style={{ display: "flex", alignItems: "center", gap: 7, flex: "0 1 auto", minWidth: 0 }}
          >
            <span
              style={{
                fontSize: 13.5,
                fontWeight: 700,
                color: "var(--text-strong)",
                overflowWrap: "anywhere",
              }}
            >
              {role.label}
            </span>
            <HintMark text={`${hint}.`} />
          </span>
          <span
            style={{
              minWidth: 0,
              flex: "1 1 220px",
              display: "flex",
              alignItems: "center",
              columnGap: 12,
              rowGap: 4,
              flexWrap: "wrap",
            }}
          >
            {assigned.length === 0 ? (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: role.required ? "var(--hm-warning)" : "var(--text-muted)",
                }}
              >
                {role.slots.length === 0
                  ? "noch keiner"
                  : role.required
                    ? "Pflicht · kein Zähler"
                    : "optional · leer"}
              </span>
            ) : (
              assigned.map((meter) => (
                <span
                  key={meter.id}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}
                >
                  <span
                    style={{ display: "inline-flex", flexShrink: 0, color: "var(--text-muted)" }}
                  >
                    {roleIcon(role)}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--text-strong)",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {meter.label}
                  </span>
                  {meter.status !== "draft" && meter.status !== "active" && (
                    <Badge tone="warning">
                      {meter.status === "archived" ? "archiviert" : "abgelöst"}
                    </Badge>
                  )}
                </span>
              ))
            )}
            {assigned.length > 0 && open.length > 0 && (
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--hm-warning)" }}>
                {open.length === 1 ? "einer ohne Zähler" : `${open.length} ohne Zähler`}
              </span>
            )}
          </span>
          {behind.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const own = ownSlot === undefined ? null : behindGroup(ownSlot.key);
                openGroup(
                  "list",
                  own ?? {
                    key: `${role.key}:dahinter`,
                    label: role.label,
                    slots: behind.map((slot) => slot.key),
                    kind: "behind",
                    siteAreaId: null,
                  },
                );
              }}
            >
              {behind.length} Verbraucher
              <icons.IconChevronRight size={14} />
            </Button>
          )}
          {/* Ohne Schreibrecht nur dort, wo es etwas anzusehen gibt (HM-160). */}
          {(!readOnly || assigned.length > 0 || role.slots.length > 1) && (
            <Button
              variant={assigned.length === 0 && role.slots.length === 1 ? "primary" : "secondary"}
              size="sm"
              aria-expanded={selected}
              onClick={() => openRole(role)}
            >
              {role.slots.length === 0
                ? "Hinzufügen"
                : assigned.length === 0 && role.slots.length === 1
                  ? "Zähler wählen"
                  : "Ansehen"}
            </Button>
          )}
        </div>
        {selected && renderPanel("list", "0 18px 16px")}
      </div>
    );
  };

  return (
    <>
      {/* Kopf, Schema und Zählerwahl in einer Karte: Der Klick ins Bild und die
          Auswahl, die daraus folgt, dürfen nicht auf zwei Karten liegen. */}
      <Card padding="none">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-4)",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "16px 18px 0",
          }}
        >
          <div
            style={{
              minWidth: 0,
              flex: "1 1 300px",
              display: "flex",
              flexDirection: "column",
              gap: 5,
            }}
          >
            <span style={CAP}>Messkonzept Strom</span>
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 19,
                fontWeight: 700,
                letterSpacing: "var(--tracking-tight)",
                color: "var(--text-strong)",
              }}
            >
              {template.label}
            </span>
            <span style={{ fontSize: 13, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {template.description}
            </span>
            <span style={{ ...MONO, fontSize: 11.5, color: "var(--text-muted)", paddingTop: 2 }}>
              Konzept gültig ab {formatInstant(new Date(since), timeZone, "de-DE")}
            </span>
          </div>
          <div
            style={{ display: "flex", alignItems: "center", gap: "var(--sp-2)", flexWrap: "wrap" }}
          >
            {missing.length === 0 ? (
              <Badge tone="success">Vollständig</Badge>
            ) : (
              <Badge tone="warning">
                {missing.length === 1
                  ? "Ein Pflichtsteckplatz offen"
                  : `${missing.length} Pflichtsteckplätze offen`}
              </Badge>
            )}
            {!readOnly && (
              <Button variant="secondary" size="sm" onClick={() => setPicking((open) => !open)}>
                {picking ? "Abbrechen" : "Konzept ändern"}
              </Button>
            )}
          </div>
        </div>

        {picking && !readOnly && (
          <div style={{ padding: "var(--sp-4) 18px 0" }}>
            <TemplatePicker
              siteId={siteId}
              templates={templates}
              current={template.key}
              since={firstStart}
              latestChange={concept.validFrom}
              timeZone={timeZone}
              onDone={() => setPicking(false)}
            />
          </div>
        )}

        {/* **Die Vorlage rechnet anders als diese Fassung** (HM-086). Der Graph
            einer Fassung steht fest; die Formeln kommen aus der Vorlage von
            heute. Ohne den Hinweis zeigte die Seite eine Rechnung, die dahinter
            nicht läuft — und niemand sähe es den Zahlen an. */}
        {concept.graphOutdated && (
          <div style={{ padding: "var(--sp-3) 18px 0" }}>
            <Alert
              tone="warning"
              action={
                readOnly ? undefined : (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={save.isPending}
                    onClick={() =>
                      save.mutate(template.layout === null ? { inputs: assignment } : { layout })
                    }
                  >
                    Auf heutige Rechnung umstellen
                  </Button>
                )
              }
            >
              <strong>Dieses Messkonzept rechnet noch nach der früheren Vorlage.</strong> Die
              Formeln unter „So wird gerechnet" sind die heutigen; die Werte dahinter entstehen
              weiter nach der alten Rechnung, bis du umstellst. Die Zuordnung der Steckplätze bleibt
              dabei erhalten.
            </Alert>
          </div>
        )}

        {/* **Der Erzeugungszähler zählt heute anders als diese Fassung** (#42). Ob er die
            Aufnahme zählt, folgt aus ihm — ein Speicher kam dazu, ein Zählwerk wurde
            abgeschaltet. Übernommen wird es ab einem Zeitpunkt, den jemand bestätigt; die
            Rechnung ändert sich nicht nebenbei. */}
        {intakeTexts !== null && (
          <div style={{ padding: "var(--sp-3) 18px 0" }}>
            {adoptingIntake && !readOnly ? (
              <ValidFromChoice
                question={intakeTexts.question}
                note={intakeTexts.note}
                acknowledge="Mir ist bewusst, dass sich die Berechnung ab dem Zeitpunkt ändert."
                submitLabel="Übernehmen"
                conceptStart={conceptStart}
                latestChange={concept.validFrom}
                timeZone={timeZone}
                busy={save.isPending}
                onCommit={(validFrom) => {
                  /* Übernommen wird je Erzeugungszähler (#43): Wer es kann, zählt, wer es nicht
                       mehr kann, nicht. */
                  save.mutate({
                    adoptIntake: true,
                    ...(validFrom === null ? {} : { validFrom }),
                  });
                  setAdoptingIntake(false);
                }}
                onCancel={() => setAdoptingIntake(false)}
                cancelLabel="Abbrechen"
              />
            ) : (
              <Alert
                tone="info"
                action={
                  readOnly ? undefined : (
                    <Button variant="secondary" size="sm" onClick={() => setAdoptingIntake(true)}>
                      Übernehmen …
                    </Button>
                  )
                }
              >
                {intakeTexts.notice}
              </Alert>
            )}
          </div>
        )}

        {missingUnits.length > 0 && (
          <div style={{ padding: "var(--sp-3) 18px 0" }}>
            <Alert
              tone="info"
              action={
                readOnly ? undefined : (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => save.mutate({ layout })}
                  >
                    Wohneinheiten übernehmen
                  </Button>
                )
              }
            >
              {missingUnits.length === 1
                ? `Die Wohneinheit „${missingUnits[0]?.name}" ist noch nicht im Messkonzept.`
                : `${missingUnits.length} Wohneinheiten sind noch nicht im Messkonzept.`}{" "}
              Übernehmen nimmt die heutigen Wohneinheiten auf; die Zuordnung der übrigen Steckplätze
              bleibt.
            </Alert>
          </div>
        )}

        <p style={{ ...MUTED, margin: 0, padding: "10px 18px 0" }}>
          {readOnly
            ? "Steckplatz im Bild antippen, um seinen Zähler anzusehen — ein Platz trägt genau einen Zähler"
            : "Steckplatz im Bild antippen, um einen Zähler zuzuordnen — ein Platz trägt genau einen Zähler"}
          {!readOnly && structure.roles.some((role) => role.add !== null)
            ? ", weitere kommen über das Plus derselben Rolle hinzu"
            : ""}
          . Gestrichelt umrandet, was noch offen ist
          {behindHouse || template.layout?.consumerUnits === true
            ? '; der Kasten „Verbraucher" nennt, was ein Zähler aufschlüsselt.'
            : "."}
        </p>
        <div style={{ padding: "12px 18px 4px" }}>
          <ConceptSketch
            template={template}
            meterNameOf={(slot) => meterOfSlot(slot)?.label ?? null}
            assetsOf={assetsOfSlot}
            activeKey={sketchKey}
            onPick={onSketchPick}
            readOnly={readOnly}
          />
        </div>

        {panel !== null && panel.from === "schema" && (
          <div style={{ margin: "4px 18px 0", borderTop: "1px solid var(--border-subtle)" }}>
            {renderPanel("schema", "14px 0 4px")}
          </div>
        )}

        <div style={{ height: "var(--sp-3)" }} />

        {/* Die Verteilregel des PV-Stroms (HM-097, ADR 0027): nur, wo die Vorlage
            mehr als eine anbietet. Eingeklappt mit der geltenden Regel in der
            Zeile — sie wird einmal eingerichtet und danach selten angefasst. */}
        {distributions.length > 1 && !readOnly && (
          <>
            <Disclosure
              id={`${ids}-split`}
              title="Verteilung des PV-Stroms"
              summary={distributionLabel(layout.distribution)}
              open={showSplit}
              onToggle={() => setShowSplit((open) => !open)}
            />
            {showSplit && (
              <div id={`${ids}-split`} style={{ padding: "0 18px 16px" }}>
                <DistributionChoice
                  distributions={distributions}
                  current={layout.distribution}
                  conceptStart={conceptStart}
                  latestChange={concept.validFrom}
                  timeZone={timeZone}
                  busy={save.isPending}
                  error={save.isError && panel === null ? save.error.message : null}
                  onCommit={(distribution, validFrom) =>
                    save.mutate({
                      layout: { ...layout, distribution },
                      ...(validFrom === null ? {} : { validFrom }),
                    })
                  }
                />
              </div>
            )}
          </>
        )}

        {/* Der Rechenweg ist die Begründung für die Konzeptwahl — beim Einrichten
            wichtig, im Alltag nicht. Eingeklappt, aber mit der ersten Formel in
            der Zeile: Ein falsch gestecktes Konzept soll auffallen, ohne dass
            jemand danach sucht. */}
        <Disclosure
          id={`${ids}-math`}
          title="So wird gerechnet"
          summary={template.formulas[0]}
          mono
          open={showMath}
          onToggle={() => setShowMath((open) => !open)}
        />
        {showMath && (
          <div
            id={`${ids}-math`}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              margin: "0 18px 16px",
              padding: "12px 14px",
              borderRadius: "var(--radius-md)",
              background: "var(--surface-inset)",
            }}
          >
            {template.formulas.map((formula) => (
              <span key={formula} style={{ ...MONO, fontSize: 13, color: "var(--text-strong)" }}>
                {formula}
              </span>
            ))}
            <span style={{ ...MUTED, fontSize: 12, marginTop: 2 }}>
              Gerechnet wird nur aus den Zählern in den Steckplätzen. Ein leerer Pflichtsteckplatz
              rechnet nicht falsch, sondern gar nicht — was von ihm abhängt, fehlt.
            </span>
            {distributions.length > 1 && (
              <span style={{ ...MUTED, fontSize: 12 }}>
                Verteilung auf die Wohneinheiten nach{" "}
                <strong style={{ color: "var(--text-body)" }}>
                  {distributionLabel(layout.distribution)}
                </strong>
                .
              </span>
            )}
          </div>
        )}

        {/* Der Verlauf ist die Gegenprobe zur Gültigkeit: Wer wissen will, warum
            ein Zeitraum anders gerechnet ist, findet hier den Zeitpunkt. */}
        <Disclosure
          id={`${ids}-log`}
          title="Verlauf"
          summary={`${history.length === 1 ? "eine Änderung" : `${history.length} Änderungen`}${
            latestChange === undefined
              ? ""
              : ` · zuletzt ab ${formatInstant(new Date(latestChange.at), timeZone, "de-DE")}`
          }`}
          open={showLog}
          onToggle={() => setShowLog((open) => !open)}
        />
        {showLog && (
          <div
            id={`${ids}-log`}
            style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 18px 16px" }}
          >
            <ConceptHistoryList history={history} timeZone={timeZone} />
            <RecomputeLine
              concept={concept}
              siteId={siteId}
              timeZone={timeZone}
              readOnly={readOnly}
            />
          </div>
        )}
      </Card>

      {/* Die Liste: offene Steckplätze stehen für sich, fertige liegen
          eingeklappt darunter. Bei einem Mehrfamilienhaus ist das der
          Unterschied zwischen einer Seite und drei. */}
      <Card padding="none">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-3)",
            alignItems: "baseline",
            justifyContent: "space-between",
            padding: "14px 18px 12px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
            <h2
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: 16,
                fontWeight: 700,
                color: "var(--text-strong)",
              }}
            >
              Abrechnungsrelevante Zähler
            </h2>
            <span style={MUTED}>Nur diese Zähler gehen in die Bilanz ein.</span>
          </div>
          <span style={{ fontSize: 12.5, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
            {doneRoles.length} von {structure.roles.length} zugeordnet
          </span>
        </div>

        {todoRoles.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 3, padding: "0 18px 10px" }}>
            <span style={{ ...CAP, color: "var(--hm-warning)" }}>Noch zu tun</span>
            <span style={MUTED}>
              Solange ein Pflichtsteckplatz leer bleibt, werden die betroffenen Größen nicht
              berechnet — sie fehlen, statt falsch zu sein.
            </span>
          </div>
        )}
        {todoRoles.map(roleRow)}

        {restRoles.length > 0 && (
          <Disclosure
            id={`${ids}-done`}
            title={`${doneRoles.length} zugeordnet${freeRoles.length > 0 ? ` · ${freeRoles.length} frei` : ""}`}
            summary={restRoles.map((role) => role.label).join(" · ")}
            open={showDone}
            onToggle={() => setShowDone((open) => !open)}
          />
        )}
        {showDone && <div id={`${ids}-done`}>{restRoles.map(roleRow)}</div>}

        {/* „Verbrauch im Haus" ist kein Steckplatz — er trägt keinen Zähler, aber
            die Verbraucher, die ihn aufschlüsseln. Darum eine eigene Zeile am
            Ende der Liste und nicht zwischen den Steckplätzen. */}
        {behindHouse && (
          <div style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "var(--sp-3)",
                alignItems: "center",
                padding: "11px 18px",
                background: listRow === HOUSE_KEY ? "var(--surface-inset)" : "transparent",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 7, flex: "0 0 auto" }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text-strong)" }}>
                  Verbrauch im Haus
                </span>
                <HintMark text="Kein Zähler, sondern das Ergebnis der Bilanz. Hier stehen die Verbraucher mit eigenem Zähler, etwa eine Wallbox — sie schlüsseln den Verbrauch auf und ändern die Summen nicht." />
              </span>
              <span
                style={{
                  minWidth: 0,
                  flex: "1 1 220px",
                  fontSize: 13,
                  color: houseMissing.length > 0 ? "var(--hm-warning)" : "var(--text-muted)",
                  fontWeight: houseMissing.length > 0 ? 600 : 400,
                }}
              >
                {houseConsumers.length === 0
                  ? "kein Verbraucher hinterlegt"
                  : houseConsumers.map((slot) => slot.label).join(" · ")}
                {houseMissing.length > 0 &&
                  ` — ${houseMissing.length === 1 ? "einer ohne Zähler" : `${houseMissing.length} ohne Zähler`}`}
              </span>
              {(!readOnly || houseConsumers.length > 0) && (
                <Button
                  variant="secondary"
                  size="sm"
                  aria-expanded={listRow === HOUSE_KEY}
                  onClick={() => openHouse("list")}
                >
                  {houseConsumers.length === 0 ? "Verbraucher hinterlegen" : "Verbraucher"}
                </Button>
              )}
            </div>
            {listRow === HOUSE_KEY && renderPanel("list", "0 18px 16px")}
          </div>
        )}
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Die Verteilregel zur Wahl. **Die Beschreibung steht neben der Wahl, nicht in
 * einem Tooltip**: Welche Regel gilt, entscheidet der Vertrag mit den Mietern,
 * und wer hier wählt, soll lesen, was er damit zusagt. Ein Regelwechsel ist
 * eine neue Fassung ab einem Datum — der Vertrag kann sich ändern, und die Zeit
 * davor bleibt, wie sie gerechnet wurde.
 */
function DistributionChoice({
  distributions,
  current,
  conceptStart,
  latestChange,
  timeZone,
  busy,
  error,
  onCommit,
}: {
  distributions: readonly PvDistribution[];
  current: PvDistribution;
  conceptStart: string;
  latestChange: string;
  timeZone: string;
  busy: boolean;
  error: string | null;
  onCommit: (distribution: PvDistribution, validFrom: string | null) => void;
}) {
  const name = useId();
  const [pending, setPending] = useState<PvDistribution>(current);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <span style={{ ...MUTED, fontSize: 12 }}>
        Die Messung ist bei jeder Regel dieselbe. Unterschiedlich ist, wie die gemessene Erzeugung
        den Wohneinheiten zugerechnet wird — und damit, was jede Einheit bezahlt.
      </span>
      <div
        role="radiogroup"
        aria-label="Verteilung des PV-Stroms"
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
      >
        {distributions.map((distribution) => {
          const on = pending === distribution;
          return (
            <label
              key={distribution}
              style={{
                display: "flex",
                gap: 11,
                alignItems: "flex-start",
                padding: "10px 13px",
                cursor: "pointer",
                borderRadius: "var(--radius-md)",
                background: on ? "var(--surface-inset)" : "transparent",
                border: `1px solid ${on ? "var(--hm-green-500)" : "var(--border-subtle)"}`,
              }}
            >
              <input
                type="radio"
                name={name}
                value={distribution}
                checked={on}
                onChange={() => setPending(distribution)}
                style={{ marginTop: 3, accentColor: "var(--hm-green-500)" }}
              />
              <span
                style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 3 }}
              >
                <span
                  style={{ fontSize: 13, fontWeight: on ? 700 : 600, color: "var(--text-strong)" }}
                >
                  {distributionLabel(distribution)}
                  {distribution === current && (
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--hm-green-700)" }}>
                      {" "}
                      · gilt heute
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text-muted)" }}>
                  {distributionDescription(distribution)}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      {pending !== current && (
        <ValidFromChoice
          question="Ab wann gilt die neue Verteilung?"
          note={`Ab diesem Zeitpunkt wird der PV-Strom nach „${distributionLabel(pending)}" zugerechnet. Zeiträume davor bleiben nach der bisherigen Regel gerechnet.`}
          submitLabel="Verteilung übernehmen"
          conceptStart={conceptStart}
          latestChange={latestChange}
          timeZone={timeZone}
          busy={busy}
          onCommit={(validFrom) => onCommit(pending, validFrom)}
          onCancel={() => setPending(current)}
        />
      )}
      {error !== null && <Alert tone="danger">{error}</Alert>}
    </div>
  );
}

/**
 * Die Fassungen als Verlauf (`lib/concept-history.ts`). Eine Korrektur trägt
 * „rückwirkend", eine von ihr ganz ersetzte Fassung steht blasser da — sie hat
 * nie gerechnet und bleibt trotzdem sichtbar: Wer die Zahl von gestern mit der
 * von heute vergleicht, findet hier den Grund.
 */
function ConceptHistoryList({
  history,
  timeZone,
}: {
  history: readonly ConceptChange[];
  timeZone: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-subtle)",
        overflow: "hidden",
      }}
    >
      {history.map((entry, index) => (
        <div
          key={entry.id}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-3)",
            alignItems: "baseline",
            padding: "10px 13px",
            background: index % 2 === 0 ? "var(--surface-inset)" : "transparent",
            opacity: entry.replaced ? 0.7 : 1,
          }}
        >
          <span
            style={{ ...MONO, fontSize: 11.5, color: "var(--text-muted)", whiteSpace: "nowrap" }}
          >
            {formatInstant(new Date(entry.at), timeZone, "de-DE")}
          </span>
          <span
            style={{ minWidth: 0, flex: "1 1 200px", fontSize: 12.5, color: "var(--text-body)" }}
          >
            {entry.text}
            {entry.replaced && (
              <span style={{ color: "var(--text-muted)" }}>
                {" "}
                — durch eine spätere Korrektur ersetzt
              </span>
            )}
          </span>
          {entry.retroactive && <Badge tone="neutral">rückwirkend</Badge>}
          <span
            style={{ ...MONO, fontSize: 11.5, color: "var(--text-muted)", whiteSpace: "nowrap" }}
          >
            Nr. {entry.version}
          </span>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Weitere Verbraucher und Erzeuger einer Familien-Vorlage (HM-080): Was hinter
 * dem Summenzähler außer den Wohnungen noch zählt — Allgemeinstrom,
 * Wärmepumpe, Wallbox — und wie viele Erzeugungszähler es gibt. Jede Änderung
 * ist eine neue Fassung mit ihren Steckplätzen; die Wohneinheiten stehen nicht
 * hier, sie kommen aus der Anlage.
 *
 * **Die Art trägt genau eine fachliche Folge:** Ein Verbraucher der Art
 * „Allgemeinstrom" ist der Allgemeinstrom, und das Konzept rechnet dann keinen
 * Rest mehr. Bei „mit Eigenverbrauch" wird je Verbraucher gefragt, ob er vor
 * oder hinter dem Eigen-Zähler hängt — die Formel hängt daran.
 *
 * **Es ist keine Karte mehr, sondern steht dort, wo gefragt wird** (HM-114):
 * unter dem Plus eines Strangs („+ Verbraucherzähler", „+ Erzeugungszähler"),
 * unter dem Verbraucher-Kasten einer Wohneinheit und unter „Verbrauch im Haus". Der
 * Entwurf hat unter der Konzeptkarte nur die Liste; eine eigene Karte
 * „Weitere Verbraucher" wäre ein zweiter Ort für dieselbe Handlung. `focus`
 * zeigt nur den Teil, nach dem der Einstieg fragt. Die Verteilregel steht
 * ebenfalls nicht hier, sondern als eigene Zeile der Konzeptkarte
 * (`DistributionChoice`); gespeichert wird sie weiter als Layout, und dieses
 * Formular schickt sie unverändert mit.
 *
 * **Und ein Verbraucher kann einer Wohneinheit gehören** (HM-111): Er hängt
 * dann hinter ihrem Zähler — die Wallbox eines Mieters — und schlüsselt ihren
 * Verbrauch auf. Die Lage vor oder hinter dem Eigen-Zähler folgt daraus und
 * wird nicht mehr gefragt.
 */
function LayoutEditor({
  template,
  layout,
  conceptStart,
  latestChange,
  timeZone,
  units,
  slotOfConsumer,
  meterOfSlotKey,
  unitOfMeter,
  busy,
  error,
  onCommit,
  embedded,
}: {
  /**
   * Eingebettet in ein Panel der Konzeptkarte: ohne Karte, gleich im Bearbeiten,
   * „Abbrechen" schließt. `draft` bereitet den Entwurf vor — ein neuer
   * Verbraucher, ein weiterer Erzeuger.
   */
  embedded: {
    readonly onClose: () => void;
    readonly draft?: (layout: Layout) => Layout;
    /** Nur dieser Teil des Layouts — das Plus eines Strangs fragt nach genau einem. */
    readonly focus?: "consumers" | "generators";
  };
  template: Template;
  layout: Layout;
  conceptStart: string;
  latestChange: string;
  timeZone: string;
  /** Die Wohneinheiten, denen sich ein Verbraucher zuordnen lässt — leer, wo die Vorlage das nicht kennt. */
  units: readonly SiteArea[];
  /** Der Steckplatz eines Verbrauchers dieser Fassung — `null`, wenn er neu ist. */
  slotOfConsumer: (consumerKey: string) => string | null;
  /** Der Zähler, der heute in einem Steckplatz dieser Fassung steckt. */
  meterOfSlotKey: (slotKey: string) => Meter | null;
  unitOfMeter: (meter: Meter) => string | null;
  busy: boolean;
  error: string | null;
  /**
   * Speichern — mit den Messstellen, die vorher ihrer Wohneinheit zugeordnet
   * werden sollen; scheitert das Speichern, stellt der Aufrufer sie zurück.
   */
  onCommit: (layout: Layout, validFrom: string | null, moves: readonly UnitMove[]) => void;
}) {
  const spec = template.layout;
  const [draft, setDraft] = useState<Layout>(() => embedded.draft?.(layout) ?? layout);
  const positions = spec?.consumerPositions === true;
  /**
   * **Eine Hausvorlage fragt nach Verbrauchern** (HM-110) **und nur die
   * Überschusseinspeisung nach der Zahl der Erzeugungszähler** (#43). Die
   * übrigen führen ihren Erzeugungszähler selbst, und die Anzahl steht fest —
   * eine Auswahl mit einem Eintrag wäre eine Frage ohne Antwortmöglichkeit. Und ein Haus hat
   * keinen Summenzähler und keine Wohneinheiten; die Texte sagen dann, was
   * hier gemeint ist: Wallbox, Wärmepumpe, was einen eigenen Zähler hat.
   */
  const generatorsFixed = spec === null || spec.generators.min === spec.generators.max;
  const house = spec !== null && !spec.perUnit;
  const showGenerators =
    !generatorsFixed && (embedded.focus === undefined || embedded.focus === "generators");
  const showConsumers = embedded.focus === undefined || embedded.focus === "consumers";

  const changed = JSON.stringify(draft) !== JSON.stringify(layout);
  /* Bei einem Verbraucher mit Wohneinheit folgt die Lage aus ihr (HM-111). */
  const complete = draft.consumers.every(
    (c) =>
      c.label.trim().length > 0 && (!positions || c.position !== null || c.siteAreaId !== null),
  );
  const unitName = (id: string | null): string | null =>
    units.find((unit) => unit.id === id)?.name ?? null;

  /**
   * **Die Messstelle eines zugeordneten Verbrauchers muss seiner Wohneinheit
   * gehören** (HM-111) — wie die im Steckplatz der Wohnung selbst. Wer die
   * Wohneinheit hier wählt, hat die Antwort gerade gegeben; deshalb wird
   * gefragt und vor dem Speichern in die Messstelle geschrieben, statt den
   * Server mit `422` antworten zu lassen (dieselbe Rückfrage wie HM-082).
   */
  const [unitPrompt, setUnitPrompt] = useState<{
    validFrom: string | null;
    items: { label: string; meter: Meter; to: string; from: string | null }[];
  } | null>(null);
  const meterOfConsumer = (consumerKey: string): Meter | null => {
    const slotKey = slotOfConsumer(consumerKey);
    return slotKey === null ? null : meterOfSlotKey(slotKey);
  };
  /**
   * **Was ein entfernter Verbraucher oder eine kleinere Erzeugerzahl
   * mitnimmt** (HM-190): Steckt in einem der wegfallenden
   * Steckplätze ein Zähler, ist die Änderung ein „Zuordnung aufheben" — und
   * verlangt denselben Haken wie dort.
   */
  const droppedMeters = droppedOccupiedSlots(
    template.slots,
    layout,
    draft,
    (slotKey) => meterOfSlotKey(slotKey) !== null,
    slotOfConsumer,
  ).flatMap((slotKey) => {
    const meter = meterOfSlotKey(slotKey);
    return meter === null ? [] : [meter.label];
  });
  const unitConflicts = draft.consumers.flatMap((consumer) => {
    if (consumer.siteAreaId === null || consumer.key === undefined) return [];
    const meter = meterOfConsumer(consumer.key);
    if (meter === null) return [];
    const from = unitOfMeter(meter);
    return from === consumer.siteAreaId
      ? []
      : [{ label: consumer.label, meter, to: consumer.siteAreaId, from }];
  });
  const commit = (validFrom: string | null) => {
    if (unitConflicts.length > 0) {
      setUnitPrompt({ validFrom, items: unitConflicts });
      return;
    }
    onCommit(draft, validFrom, []);
  };
  /** Zuordnen und speichern als ein Vorgang — zurückgestellt wird beim Aufrufer (`lib/unit-moves.ts`). */
  const confirmUnits = () => {
    if (unitPrompt === null) return;
    const prompt = unitPrompt;
    setUnitPrompt(null);
    onCommit(
      draft,
      prompt.validFrom,
      prompt.items.map((item) => ({
        meteringPointId: item.meter.meteringPointId,
        meterLabel: item.meter.label,
        to: item.to,
        from: item.from,
      })),
    );
  };

  const update = (index: number, patch: Partial<LayoutConsumer>) =>
    setDraft((current) => ({
      ...current,
      consumers: current.consumers.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    }));

  const editor = (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      {showGenerators && (
        <div style={{ maxWidth: 260 }}>
          <Select
            label="Erzeugungszähler"
            value={String(draft.generatorCount)}
            onChange={(event) => setDraft({ ...draft, generatorCount: Number(event.target.value) })}
            options={Array.from(
              { length: (spec?.generators.max ?? 1) - (spec?.generators.min ?? 1) + 1 },
              (_, i) => {
                const n = (spec?.generators.min ?? 1) + i;
                return { value: String(n), label: n === 1 ? "ein Zähler" : `${n} Zähler` };
              },
            )}
          />
        </div>
      )}

      {showConsumers &&
        draft.consumers.map((consumer, index) => (
          <div
            key={consumer.key ?? `neu-${index}`}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))",
              gap: "var(--sp-2)",
              alignItems: "end",
              padding: "10px 12px",
              borderRadius: "var(--radius-md)",
              background: "var(--surface-inset)",
            }}
          >
            <Input
              label="Bezeichnung"
              value={consumer.label}
              maxLength={120}
              onChange={(event) => update(index, { label: event.target.value })}
            />
            <Select
              label="Art"
              value={consumer.kind}
              onChange={(event) => {
                const kind = event.target.value as ConsumerKind;
                // Allgemeinstrom gehört keiner Wohnung.
                update(index, kind === "common_area" ? { kind, siteAreaId: null } : { kind });
              }}
              options={CONSUMER_KINDS.map((kind) => ({
                value: kind,
                label: t(`consumer.kind.${kind}`, "de-DE"),
              }))}
            />
            {units.length > 0 && consumer.kind !== "common_area" && (
              <Select
                label="Wohneinheit"
                value={consumer.siteAreaId ?? ""}
                onChange={(event) =>
                  update(index, {
                    siteAreaId: event.target.value === "" ? null : event.target.value,
                    // Die Lage folgt aus der Wohneinheit; eine alte stünde im Widerspruch.
                    position: null,
                  })
                }
                options={[
                  { value: "", label: "keine — Verbraucher des Hauses" },
                  ...units.map((unit) => ({ value: unit.id, label: unit.name })),
                ]}
              />
            )}
            {positions && consumer.siteAreaId === null && (
              <Select
                label="Lage"
                value={consumer.position ?? ""}
                onChange={(event) =>
                  update(index, {
                    position:
                      event.target.value === "" ? null : (event.target.value as ConsumerPosition),
                  })
                }
                options={[
                  { value: "", label: "bitte wählen" },
                  ...CONSUMER_POSITIONS.map((position) => ({
                    value: position,
                    label: t(`consumer.position.${position}`, "de-DE"),
                  })),
                ]}
              />
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setDraft({ ...draft, consumers: draft.consumers.filter((_, i) => i !== index) })
              }
            >
              Entfernen
            </Button>
          </div>
        ))}

      {showConsumers && (
        <div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setDraft({
                ...draft,
                consumers: [
                  ...draft.consumers,
                  {
                    kind: house ? "wallbox" : "heat_pump",
                    label: "",
                    position: null,
                    siteAreaId: null,
                  },
                ],
              })
            }
          >
            <icons.IconPlus size={15} />
            Verbraucher hinzufügen
          </Button>
        </div>
      )}

      {changed && complete && (
        <ValidFromChoice
          /* Ein neuer Satz zum Haken ist eine neue Bestätigung — der alte Haken gilt nicht für ihn. */
          key={droppedMeters.join("|")}
          question="Ab wann gilt die Änderung?"
          note="Jeder Verbraucher bekommt einen eigenen Steckplatz; ein entfernter nimmt seinen mit. Zeiträume davor bleiben, wie sie gerechnet wurden."
          acknowledge={
            droppedMeters.length === 0
              ? undefined
              : droppedMeters.length === 1
                ? `Mir ist bewusst, dass „${droppedMeters[0]}" ab dem Zeitpunkt nicht mehr in die Rechnung eingeht.`
                : `Mir ist bewusst, dass ${droppedMeters.map((label) => `„${label}"`).join(", ")} ab dem Zeitpunkt nicht mehr in die Rechnung eingehen.`
          }
          submitLabel="Änderung übernehmen"
          conceptStart={conceptStart}
          latestChange={latestChange}
          timeZone={timeZone}
          busy={busy}
          onCommit={commit}
        />
      )}
      {unitPrompt !== null && (
        <Alert
          tone="info"
          action={
            <Button size="sm" disabled={busy} onClick={confirmUnits}>
              Zuordnen und speichern
            </Button>
          }
        >
          {unitPrompt.items.map((item) => (
            <span key={item.meter.id} style={{ display: "block" }}>
              <strong>
                Der Zähler „{item.meter.label}" ({item.label}) ist{" "}
                {item.from === null
                  ? "noch keiner Wohneinheit zugeordnet."
                  : `der Wohneinheit „${unitName(item.from) ?? "andere Wohneinheit"}" zugeordnet.`}
              </strong>{" "}
              Er hängt hinter dem Zähler von „{unitName(item.to) ?? "Wohneinheit"}" — soll seine
              Messstelle dieser Wohneinheit zugeordnet werden?
            </span>
          ))}
          <span style={{ display: "block", marginTop: 4 }}>
            Die Zuordnung gilt für die Messstelle und bleibt bei einem Zählerwechsel bestehen.
          </span>
        </Alert>
      )}
      {error !== null && <Alert tone="danger">{error}</Alert>}
      <div style={{ display: "flex", gap: "var(--sp-2)" }}>
        <Button variant="ghost" size="sm" disabled={busy} onClick={embedded.onClose}>
          Abbrechen
        </Button>
      </div>
    </div>
  );

  return editor;
}

/** Das Symbol eines Zählers — Spartenfarbe Strom, Wegweisung und keine Datenaussage. */
function MeterChip() {
  return (
    <span
      style={{
        display: "inline-flex",
        width: 34,
        height: 34,
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "var(--radius-md)",
        background: "color-mix(in srgb, var(--hm-commodity-electricity) 18%, transparent)",
        color: "var(--text-body)",
      }}
    >
      <icons.IconGauge size={17} />
    </span>
  );
}

/**
 * Die Auswahlliste. **Belegte Zähler bleiben sichtbar, aber gesperrt** — sonst
 * sucht man einen Zähler, der nur woanders steckt, und hält ihn für gelöscht.
 * Und **unpassende auch**: Ein Zähler ohne Einspeisezählwerk kann kein
 * Netzzähler einer Überschusseinspeisung sein; er steht da und sagt, warum
 * nicht.
 *
 * Die Wahl liegt im Steckplatz selbst und nicht in einem Dialog: Man sieht beim
 * Wählen, wofür man wählt.
 */
function MeterChooser({
  slot,
  template,
  meters,
  current,
  chosen,
  chosenRoles,
  currentRoles,
  liveValues,
  currentLabel,
  taken,
  busy,
  conceptStart,
  latestChange,
  timeZone,
  padding,
  onChoose,
  onRole,
  onCommit,
  onCancel,
  cancelLabel,
  error,
  unitNote,
  suggestions,
  onAcceptSuggestion,
}: {
  slot: Slot;
  template: Template;
  meters: readonly Meter[];
  current: string | null;
  /** Der Name des Zählers, der heute steckt — für die Bestätigung eines Wechsels. */
  currentLabel: string | null;
  chosen: string | null;
  chosenRoles: Readonly<Record<string, Role>>;
  currentRoles: Readonly<Record<string, Role>>;
  liveValues: readonly LiveValue[];
  taken: ReadonlySet<string>;
  busy: boolean;
  conceptStart: string;
  latestChange: string;
  timeZone: string;
  padding: string;
  onChoose: (meterId: string) => void;
  onRole: (inputKey: string, role: Role) => void;
  onCommit: (validFrom: string | null) => void;
  onCancel: () => void;
  cancelLabel: string;
  error: string | null;
  /**
   * An einem Wohneinheits-Steckplatz: was neben einem Zähler steht, dessen
   * Messstelle nicht dieser Einheit zugeordnet ist. Er bleibt wählbar — die
   * Zuordnung wird beim Übernehmen gefragt (HM-082).
   */
  unitNote?: (meter: Meter) => string | null;
  /** Offene Anlagevorschläge, die in diesen Steckplatz passten (HM-112). */
  suggestions: readonly MeterSuggestion[];
  onAcceptSuggestion: (suggestionId: string) => void;
}) {
  const chosenMeter = chosen === null ? null : (meters.find((m) => m.id === chosen) ?? null);
  const choosable = chosenMeter === null ? [] : choosableInputs(template, slot, chosenMeter);
  const roleChanged = choosable.some(
    (input) =>
      (chosenRoles[input.key] ?? null) !== (currentRoles[input.key] ?? null) &&
      chosenRoles[input.key] !== undefined,
  );
  const changed = chosen !== null && (chosen !== current || roleChanged);
  const replaces = current !== null && chosen !== null && chosen !== current;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, padding }}>
      <span style={{ fontSize: 11.5, color: "var(--text-muted)", paddingBottom: 4 }}>
        Zähler für den Steckplatz {slot.label} wählen
      </span>

      {meters.length === 0 && suggestions.length === 0 && (
        <span style={{ ...MUTED, padding: "4px 2px" }}>
          Für diese Anlage ist noch kein Zähler angelegt. Zähler entstehen unter{" "}
          <strong>Zähler</strong>, sobald ein Gerät einen passenden Wert meldet.
        </span>
      )}

      {meters.map((meter) => {
        const here = meter.id === chosen;
        const elsewhere = !here && meter.id !== current && taken.has(meter.meteringPointId);
        const fits = fitsSlot(meter, template, slot);
        const disabled = busy || elsewhere || !fits;
        return (
          <button
            key={meter.id}
            type="button"
            disabled={disabled}
            onClick={() => onChoose(meter.id)}
            aria-pressed={here}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              width: "100%",
              textAlign: "left",
              padding: "9px 12px",
              cursor: disabled ? "not-allowed" : "pointer",
              borderRadius: "var(--radius-md)",
              background: here ? "var(--hm-green-50)" : "var(--surface-card)",
              border: `1px solid ${here ? "var(--hm-green-500)" : "var(--border-subtle)"}`,
              opacity: disabled && !here ? 0.55 : 1,
              fontFamily: "var(--font-body)",
              color: "inherit",
            }}
          >
            <MeterChip />
            <span style={{ minWidth: 0, flex: 1 }}>
              <span
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--text-strong)",
                }}
              >
                {meter.label}
              </span>
              <span
                style={{ ...MONO, display: "block", fontSize: 11.5, color: "var(--text-faint)" }}
              >
                {meterNumberText(meter)} · {meterKindLabel(meter.kind)}
              </span>
            </span>
            {elsewhere && (
              <span style={{ fontSize: 11.5, color: "var(--text-faint)", whiteSpace: "nowrap" }}>
                bereits belegt
              </span>
            )}
            {!elsewhere && fits && unitNote !== undefined && unitNote(meter) !== null && (
              <span style={{ fontSize: 11.5, color: "var(--text-faint)", whiteSpace: "nowrap" }}>
                {unitNote(meter)}
              </span>
            )}
            {!fits && !elsewhere && (
              <span style={{ fontSize: 11.5, color: "var(--text-faint)", whiteSpace: "nowrap" }}>
                fehlendes Zählwerk
              </span>
            )}
            {here && (
              <icons.IconCheck size={15} style={{ color: "var(--hm-green-600)", flexShrink: 0 }} />
            )}
          </button>
        );
      })}

      {suggestions.length > 0 && (
        <span style={{ fontSize: 11.5, color: "var(--text-faint)", padding: "8px 0 2px" }}>
          Noch nicht angelegt — gemeldet und unbestätigt
        </span>
      )}
      {suggestions.map((suggestion) => (
        <button
          key={suggestion.id}
          type="button"
          disabled={busy}
          onClick={() => onAcceptSuggestion(suggestion.id)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 11,
            width: "100%",
            textAlign: "left",
            padding: "9px 12px",
            cursor: busy ? "not-allowed" : "pointer",
            borderRadius: "var(--radius-md)",
            background: "var(--surface-card)",
            border: "1px dashed var(--border-default)",
            opacity: busy ? 0.55 : 1,
            fontFamily: "var(--font-body)",
            color: "inherit",
          }}
        >
          <MeterChip />
          <span style={{ minWidth: 0, flex: 1 }}>
            <span
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text-strong)",
              }}
            >
              {suggestion.proposedLabel}
            </span>
            <span style={{ ...MONO, display: "block", fontSize: 11.5, color: "var(--text-faint)" }}>
              {suggestion.deviceName}
              {suggestion.builtIntoDevice ? " · Messwerk im Gerät" : ""}
            </span>
          </span>
          {/* Die Zusage steht am Knopf: Der Klick legt den Zähler an, bevor er gewählt ist. */}
          <span style={{ fontSize: 11.5, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
            anlegen und wählen
          </span>
        </button>
      ))}

      {/* **Welches Zählwerk?** Ein Zweirichtungszähler am Wechselrichter zählt
          die Erzeugung als Einspeisung, ein einfacher Zähler hinter der Anlage
          als Bezug. Beides ist derselbe Steckplatz, und welches gilt, weiß nur,
          wer vor dem Zählerschrank stand — deshalb wird es gefragt (Regel 9). */}
      {chosenMeter !== null && choosable.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 8,
            padding: "10px 12px",
            borderRadius: "var(--radius-md)",
            background: "var(--surface-inset)",
          }}
        >
          {choosable.map((input) => {
            /* Dieselbe Regel wie beim Speichern (`roleForInput`): Was hier
               steht, ist, was übernommen wird. Die Rolle im Konzept gilt nur
               für den Zähler, der dort steckt. */
            const value = roleForInput(
              input,
              chosenMeter,
              chosenRoles[input.key] ?? (chosen === current ? currentRoles[input.key] : undefined),
            );
            return (
              <Select
                key={input.key}
                label={`Welches Zählwerk zählt „${input.label}"?`}
                value={value}
                onChange={(event) => onRole(input.key, event.target.value as Role)}
                options={input.roles
                  .filter((role) => hasRole(chosenMeter, role))
                  .map((role) => {
                    /* Der Stand steht dabei: Ein Erzeugungszähler, dessen
                       Einspeisezählwerk bei 82 MWh steht und dessen Bezug bei
                       0,3 MWh, hat seine Antwort schon gegeben. */
                    const stand = liveValues.find(
                      (v) => v.meterId === chosenMeter.id && v.role === role,
                    );
                    return {
                      value: role,
                      label:
                        stand === undefined
                          ? registerRoleLabel(role)
                          : `${registerRoleLabel(role)} · Stand ${formatValue(stand.value, stand.unit, "de-DE", stand.unit === "Wh" ? { maximumFractionDigits: 3 } : {}).full}`,
                    };
                  })}
              />
            );
          })}
          <span style={{ fontSize: 11.5, lineHeight: 1.5, color: "var(--text-faint)" }}>
            Ein Zweirichtungszähler am Wechselrichter zählt die Erzeugung meist als Einspeisung — es
            ist das Zählwerk mit dem hohen Stand, das im Betrieb steigt.
          </span>
        </div>
      )}

      {changed && (
        <ValidFromChoice
          question={
            current === null
              ? "Ab wann gilt die Zuordnung?"
              : replaces
                ? "Ab wann gilt der Zählerwechsel?"
                : "Ab wann zählt das andere Zählwerk?"
          }
          note={
            replaces
              ? `Ab diesem Zeitpunkt wird mit „${chosenMeter?.label ?? "dem neuen Zähler"}" gerechnet; davor bleibt „${currentLabel ?? "der bisherige Zähler"}" in der Rechnung.`
              : undefined
          }
          acknowledge={
            replaces
              ? `Mir ist bewusst, dass ab dem Zeitpunkt mit „${chosenMeter?.label ?? "dem neuen Zähler"}" statt „${currentLabel ?? "dem bisherigen"}" gerechnet wird.`
              : undefined
          }
          submitLabel={
            current === null
              ? "Zuordnung übernehmen"
              : replaces
                ? "Zählerwechsel übernehmen"
                : "Zählwerk übernehmen"
          }
          conceptStart={conceptStart}
          latestChange={latestChange}
          timeZone={timeZone}
          busy={busy}
          onCommit={onCommit}
        />
      )}

      {error !== null && <Alert tone="danger">{error}</Alert>}

      <div style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap", paddingTop: 6 }}>
        <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          {cancelLabel}
        </Button>
      </div>
    </div>
  );
}

/**
 * Bis wohin gerechnet ist, und die Neuberechnung von Hand — seit HM-114 im
 * Verlauf und nicht als eigene Karte: Der Entwurf hat unter der Konzeptkarte
 * nur die Liste, und beides beantwortet dieselbe Frage wie der Verlauf —
 * warum eine Zahl ist, wie sie ist.
 *
 * **Kein Wert ist kein Fehler**: Ein frisch angelegtes Konzept hat noch keinen
 * Durchlauf hinter sich, und der Worker kommt im Fünf-Minuten-Takt. **Ein
 * Fenster, das der Nutzer nicht wählt** — die letzten 30 Tage sind der
 * Zeitraum, den die Ansichten zeigen. Angenommen ist der Auftrag, nicht das
 * Ergebnis (`202`).
 */
function RecomputeLine({
  concept,
  siteId,
  timeZone,
  readOnly,
}: {
  concept: Concept;
  siteId: string;
  timeZone: string;
  readOnly: boolean;
}) {
  const client = useQueryClient();
  const recompute = useMutation({
    mutationFn: async () => {
      const to = new Date();
      const from = new Date(to.getTime() - 30 * 24 * 3_600_000);
      const result = await api.POST("/concepts/{conceptId}/recompute", {
        params: { path: { conceptId: concept.id } },
        body: { from: from.toISOString(), to: to.toISOString() },
      });
      if (!result.data) {
        throw new Error(apiErrorMessage(result) ?? "Die Neuberechnung ließ sich nicht einstellen.");
      }
      return result.data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["site-energy", siteId] });
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--sp-3)",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ ...MUTED, minWidth: 0, flex: "1 1 260px" }}>
          {concept.computedThrough === null
            ? "Noch nicht berechnet — der nächste Durchlauf kommt innerhalb weniger Minuten."
            : `Werte berechnet bis ${formatInstant(new Date(concept.computedThrough), timeZone, "de-DE")} (${formatTimeAgo(concept.computedThrough)}).`}{" "}
          Nach einer Änderung wird von selbst neu gerechnet; von Hand ist das nur nötig, wenn ein
          Gerät Werte nachgeliefert hat, die älter sind als ein Tag.
        </span>
        {!readOnly && (
          <Button
            variant="secondary"
            size="sm"
            disabled={recompute.isPending}
            onClick={() => recompute.mutate()}
          >
            Letzte 30 Tage neu berechnen
          </Button>
        )}
      </div>
      {recompute.isSuccess && (
        <span style={MUTED}>
          Wird neu gerechnet. Die Werte erscheinen unter „Strom &amp; Erzeugung", sobald das fertig
          ist — meist nach wenigen Minuten.
        </span>
      )}
      {recompute.isError && <Alert tone="danger">{recompute.error.message}</Alert>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

type When = "now" | "start" | "custom";

/**
 * **Ab wann gilt das?** Ein Umbau gilt ab jetzt; ein falsch gesteckter Zähler
 * war von Anfang an falsch; und ein Umbau, den man erst heute einträgt, war
 * vor einer Woche. Alle drei sehen in der Zuordnung gleich aus — der
 * Unterschied entscheidet, ob und wie weit die Vergangenheit neu gerechnet
 * wird, und den kennt nur, wer dabei war. Deshalb drei Wege, nicht zwei.
 *
 * **Liegt der Zeitpunkt vor der jüngsten Fassung, steht das dabei** — so sagt es
 * der Entwurf, und der Server gibt ihm recht: Eine Fassung, die ab einem
 * früheren Zeitpunkt gilt, ersetzt die späteren ganz (`supersedeActive`).
 * Rückdatieren ist der Normalfall; still geschehen soll es nicht.
 *
 * **Wo eine laufende Rechnung auf etwas anderes übergeht** — ein Zählerwechsel,
 * eine aufgehobene Zuordnung, ein Konzeptwechsel —, wird das ausdrücklich
 * bestätigt (`acknowledge`). Eine Erstzuordnung braucht das nicht: Dort geht
 * nichts verloren.
 *
 * **Der freie Zeitpunkt wird in der Zeitzone der Anlage gelesen** (HM-190,
 * Regel 3, `lib/site-time.ts`). Ein `datetime-local` kennt keine Zeitzone, und
 * `new Date(wert)` läse ihn in der des Browsers — ein stiller Versatz von einer
 * Stunde sähe später aus wie ein Zählerwechsel um die falsche Zeit. Auch `max`
 * ist „jetzt" in der Zeit der Anlage, sonst verglichen sich zwei Uhren.
 * Weicht der Browser ab, steht dabei, in welcher Zeit gelesen wird.
 */
function ValidFromChoice({
  question,
  note,
  acknowledge,
  submitLabel = "Übernehmen",
  conceptStart,
  latestChange,
  timeZone,
  busy,
  onCommit,
  onCancel,
  cancelLabel = "Abbrechen",
}: {
  question: string;
  /** Was ab dem Zeitpunkt anders ist — ein Satz unter der Wahl. */
  note?: string;
  /** Ist er gesetzt, verlangt „Übernehmen" diesen Haken. */
  acknowledge?: string;
  submitLabel?: string;
  conceptStart: string;
  /** Beginn der jüngsten Fassung — davor wirkt eine Änderung rückwirkend. */
  latestChange?: string;
  timeZone: string;
  busy: boolean;
  onCommit: (validFrom: string | null) => void;
  onCancel?: () => void;
  cancelLabel?: string;
}) {
  const name = useId();
  const [when, setWhen] = useState<When>("now");
  const [custom, setCustom] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const browserZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const customDate = custom.length === 0 ? null : instantOfSiteLocalInput(custom, timeZone);
  const customValid = customDate !== null && customDate.getTime() <= Date.now();

  const options: readonly { value: When; label: string; hint: string }[] = [
    {
      value: "now",
      label: "Ab jetzt",
      hint: "Ein Umbau von heute. Die Vergangenheit bleibt, wie sie gerechnet wurde.",
    },
    {
      value: "start",
      label: `Seit Beginn des Messkonzepts (${formatInstant(new Date(conceptStart), timeZone, "de-DE")})`,
      hint: "Eine Korrektur: So war es von Anfang an, alles wird neu gerechnet.",
    },
    {
      value: "custom",
      label: "Seit einem bestimmten Zeitpunkt",
      hint: "Ein Umbau, der schon zurückliegt. Ab dort wird neu gerechnet.",
    },
  ];

  const validFrom: string | null | undefined =
    when === "now"
      ? null
      : when === "start"
        ? conceptStart
        : customValid
          ? customDate.toISOString()
          : undefined;
  const retroactive =
    latestChange !== undefined &&
    typeof validFrom === "string" &&
    new Date(validFrom).getTime() < new Date(latestChange).getTime();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        marginTop: 8,
        padding: "12px 14px",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-inset)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-strong)" }}>{question}</span>
      <div
        role="radiogroup"
        aria-label="Gilt ab"
        style={{ display: "flex", flexDirection: "column", gap: 4 }}
      >
        {options.map((option) => (
          <label
            key={option.value}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              fontSize: 12.5,
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={when === option.value}
              onChange={() => setWhen(option.value)}
              style={{ marginTop: 3, accentColor: "var(--hm-green-500)" }}
            />
            <span>
              <span style={{ display: "block", color: "var(--text-strong)", fontWeight: 600 }}>
                {option.label}
              </span>
              <span style={{ display: "block", color: "var(--text-muted)", fontSize: 11.5 }}>
                {option.hint}
              </span>
            </span>
          </label>
        ))}
      </div>
      {when === "custom" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 280 }}>
          <Input
            label="Zeitpunkt"
            type="datetime-local"
            value={custom}
            max={siteLocalInputValue(new Date(), timeZone)}
            onChange={(event) => setCustom(event.target.value)}
          />
          {browserZone !== timeZone && (
            <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
              Dein Browser rechnet in {browserZone}, die Anlage in {timeZone}. Der Zeitpunkt gilt in
              der Zeit der Anlage.
            </span>
          )}
          {custom.length > 0 && !customValid && (
            <span style={{ fontSize: 11.5, color: "var(--hm-warning)" }}>
              Der Zeitpunkt muss in der Vergangenheit liegen.
            </span>
          )}
        </div>
      )}
      {note !== undefined && <span style={{ ...MUTED, fontSize: 12 }}>{note}</span>}
      {retroactive && latestChange !== undefined && (
        <Alert tone="warning">
          Der Zeitpunkt liegt vor der letzten Änderung vom{" "}
          {formatInstant(new Date(latestChange), timeZone, "de-DE")}. Änderungen ab dort werden
          ersetzt, und bereits gerechnete Zeiträume werden neu gerechnet.
        </Alert>
      )}
      {acknowledge !== undefined && (
        <Checkbox checked={acknowledged} onChange={setAcknowledged} label={acknowledge} />
      )}
      <div style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
        <Button
          variant="primary"
          size="sm"
          disabled={busy || validFrom === undefined || (acknowledge !== undefined && !acknowledged)}
          onClick={() => validFrom !== undefined && onCommit(validFrom)}
        >
          {submitLabel}
        </Button>
        {onCancel !== undefined && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
            {cancelLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
